import {
  BulbOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  ExclamationCircleFilled,
  InfoCircleOutlined,
  LoadingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { hasUniquenessGroup } from '@bmo/form-core';
import type { FieldValue, RepeatableRowValue } from '@bmo/form-core';
import {
  AutoComplete,
  Divider,
  Input,
  InputNumber,
  Modal,
  Select,
  Switch,
  Tooltip,
  Typography,
  type DatePickerProps,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';

import {
  type AddressSuggestion,
  DEFAULT_TEXT_MARGIN_PX,
  type WorkflowField,
  workflowsApi,
} from '@/api/workflows';
import { useSession } from '@/auth/useSession';
import { BmoCheckbox } from '@/components/BmoCheckbox';
import { BmoDatePicker } from '@/components/BmoDatePicker';
import { BmoRadioGroup } from '@/components/BmoRadioGroup';

import { isAdvisorRepCodeChoice, needsRepCodeChoice, repCodeOptionsFor } from './advisorPrefill';
import { autocompleteFor } from './autocompleteFor';
import { ClientLanguageContext, withOfferedOptions } from './clientLanguage';
import { DateFormatContext, formatPlaceholder } from './dateFormat';
import { clearOnEmptyBackspace } from './dateInputClear';
import { formatWithMask, isMaskIncomplete, maskedDigits } from './displayMask';
import { useEmailVerify } from './emailVerifyContext';
import {
  type EmailFieldIndicator,
  emailValueIndicator,
  emailValueVerdict,
  normalizeEmail,
} from './emailVerifyGate';
import {
  applyFormatMask,
  type DateRuleContext,
  isMaskedRule,
  runValidate,
  validateDateRules,
  parseTypedDate,
  validateTypedDate,
  type ValidateRule,
} from './fieldValidation';
import { formatFullAddress, formatStreetLineWithoutApartment } from './formatFullAddress';
import type { AddressRole } from './FormRenderer';
import { effectiveMaxLength, fieldLengthViolation } from './lengthRule';
import { fieldNumberRangeViolation } from './numberRange';
import {
  integerDigitCap,
  numericInputGuard,
  numericParser,
  thousandsFormatter,
} from './numericInput';
import { OptionCardGroup } from './OptionCardGroup';
import { RepeatableRowControl } from './RepeatableRowControl';
import { RequiredMark } from './RequiredMark';
import { useRestartFromBlock } from './restartContext';
import { ShowErrorsContext } from './showErrorsContext';
import { trimOnBlur } from './trimOnBlur';
import { resolveValidationMessage } from './validationMessage';

const { Text } = Typography;

/**
 * The value model moved to `@bmo/form-core` so the fill chain can read the same
 * answer map inside `PdfFunction` (ADR-0031). Re-exported here unchanged — every
 * existing `from './FieldRenderer'` import keeps resolving.
 */
export type { FieldValue, RepeatableRowValue };

/**
 * True when the advisor's `value` for `field` matches its originator-authored
 * block-continue gate. Shared by FieldRenderer (red error + alert modal) and
 * FormShell (disable Next) so both fire on exactly the same condition.
 *
 * - Single-value fields (RadioGroup / SingleSelect): the string value is one of
 *   `blockingValues` — always OR (only one value can be picked).
 * - Multi-value fields (CheckboxGroup): with operator 'and' the selection must
 *   contain ALL blocking values; otherwise ('or' / default) it need only
 *   contain ANY of them.
 */
export function isBlockingValueMatch(field: WorkflowField, value: FieldValue): boolean {
  const blocking = field.blockingValues;
  if (!blocking || blocking.length === 0) return false;
  if (typeof value === 'string') {
    return value !== '' && blocking.includes(value);
  }
  if (Array.isArray(value)) {
    const selected = value as string[];
    if (selected.length === 0) return false;
    return field.blockingValueOperator === 'and'
      ? blocking.every((b) => selected.includes(b))
      : blocking.some((b) => selected.includes(b));
  }
  return false;
}

type Props = {
  field: WorkflowField;
  value: FieldValue;
  onChange: (next: FieldValue) => void;
  language: 'en' | 'fr';
  /**
   * Preview = inputs respond to typing but no validation blocks navigation.
   * Advisor = same renderers (later we'll wire real validation + submission).
   * Today only "preview" is used; the prop is here so the eventual Advisor
   * route can reuse this file without a refactor.
   */
  mode: 'preview' | 'advisor';
  /** Shell-wide read-only: the application is locked (BMO owns it now) or the caller asked for
   *  it. Merges with the per-field `field.readOnly` flag at the single disable point below —
   *  every control this file mounts is governed by that one line. */
  readOnly?: boolean;
  /** Whether address fields may call the Canada Post proxy. Defaults to on; the public
   *  customer runtime passes false, which drops the autocomplete branch and leaves the
   *  plain Input. See FormRenderer's Props for the three reasons. */
  addressAutocomplete?: boolean;
  /** Inline-reveal lookup — only used by the CheckboxGroup case. When set,
   *  each option whose `revealFieldId` resolves to a field on the step gets
   *  that field rendered inline beside the checkbox via a recursive
   *  FieldRenderer call. Other field types ignore these. */
  getFieldById?: (id: string) => WorkflowField | undefined;
  getValueById?: (id: string) => FieldValue;
  setValueById?: (id: string, next: FieldValue) => void;
  /** Authored row layout for a borrowed field — the CheckboxGroup case uses
   *  it to replay the column grid INSIDE the reveal slot so a half-width
   *  input stays half-width when revealed. */
  getBorrowedLayout?: (id: string) => { columnCount: number; columnIndex: number } | undefined;
  /** Find another field IN THE SAME SECTION tagged with the given
   *  AddressComplete role. Used by the ShortText address-autocomplete
   *  branch to populate sibling City/Province/Postal/Apt fields after a
   *  Canada Post Retrieve. Section-scoped (not step-scoped) so two
   *  Address compounds on one step don't cross-fill. Returns undefined
   *  if no field in the section carries that role. */
  findFieldByRole?: (role: AddressRole) => WorkflowField | undefined;
  /** Renders a SECTION borrowed by an option's `revealSectionId`, for the
   *  RadioGroup / CheckboxGroup cases. `revealFieldId`'s plural: where that one
   *  borrows a single input, this borrows a whole titled block of rows.
   *
   *  Supplied by FormRenderer as a closure rather than resolved here, because
   *  rendering a section means rendering SectionBlock — which lives in
   *  FormRenderer, which already imports this file. The closure keeps the
   *  dependency one-way. Absent for callers outside the runtime, which then
   *  render no section reveal at all. `inheritedDisabled` carries the owning
   *  control's merged disable state down into the borrowed block, so a section
   *  revealed by a disabled option is disabled too. */
  renderRevealSection?: (sectionId: string, inheritedDisabled?: boolean) => ReactNode;
  /** Cross-field uniqueness lookup — supplied for ShortText fields
   *  carrying a `uniquenessGroup`. On blur, if the value matches any peer
   *  in the same group, the callback returns the peer's advisor-facing
   *  label so we can name it in the error message. Null = no conflict. */
  findUniquenessConflictMessage?: (
    field: WorkflowField,
    value: FieldValue,
    ownValueKey?: string,
  ) => string | null;
  /** The fully-qualified value-map key for this field's value — the current
   *  instance key inside a repeatable section (`inst:<sec>:<idx>:<id>`), or the
   *  bare id otherwise. Passed to the uniqueness check so it skips this exact
   *  instance instead of always assuming instance 0. */
  valueKey?: string;
  /** Resolves ANY field's value the way visibility conditions see it: an id authored in this
   *  repeatable instance reads the instance's answer, every other id reads its bare key (see
   *  `scopeValuesToInstance`). Used for cross-field rules such as a Date's
   *  `mustFollowFieldId`. Unlike `getValueById` — which qualifies every id so sibling writes land
   *  in this instance — it can reach a field outside the section. Falls back to `getValueById`. */
  getConditionValue?: (id: string) => FieldValue;
};

/**
 * Runtime renderer for a single WorkflowField. Switches on `field.type` and
 * mounts the matching AntD input. Layout-block types (StaticText / Paragraph /
 * Divider) render their content instead of an input. Help bubble — when the
 * field has a `helpTitle` + `helpBody` — surfaces a "?" icon next to the label
 * that opens a modal with the bilingual help copy.
 *
 * Mode is currently only "preview" in the app — kept on the prop so the same
 * file can be used for the eventual Advisor route once submission lands. The
 * mode flag will drive: validation-blocks-next-vs-warns, save-on-change,
 * read-only-when-locked.
 */
export function FieldRenderer({
  field,
  value,
  onChange,
  language,
  mode: _mode,
  readOnly,
  addressAutocomplete,
  getFieldById,
  getValueById,
  setValueById,
  getBorrowedLayout,
  findFieldByRole,
  renderRevealSection,
  findUniquenessConflictMessage,
  valueKey,
  getConditionValue,
}: Props) {
  // Layout blocks short-circuit before label rendering — they don't have a
  // label-above-input shape, they ARE the content.
  if (field.type === 'Divider') {
    return <Divider style={{ margin: '8px 0' }} />;
  }
  if (field.type === 'StaticText') {
    // Inline variant — StaticText is typically a one-line heading or note;
    // we don't want react-markdown's `<p>` margin fighting the wrapper.
    return (
      <RichText
        body={contentFor(field, language)}
        variant="inline"
        style={{ display: 'block', ...textStyleFor(field) }}
      />
    );
  }
  if (field.type === 'Paragraph') {
    // Block variant — Paragraph can span multiple lines; allow `<br>` from
    // newlines and full markdown (links, bold).
    return (
      <RichText body={contentFor(field, language)} variant="block" style={textStyleFor(field)} />
    );
  }
  if (field.type === 'BulletedList') {
    return <BulletedListRenderer field={field} language={language} />;
  }
  if (field.type === 'Notice') {
    return <NoticeRenderer field={field} language={language} />;
  }
  if (field.type === 'Image') {
    return <ImageRenderer field={field} language={language} />;
  }

  return (
    <ValidatedInputField
      field={field}
      value={value}
      onChange={onChange}
      language={language}
      mode={_mode}
      readOnly={readOnly}
      addressAutocomplete={addressAutocomplete}
      getFieldById={getFieldById}
      getValueById={getValueById}
      setValueById={setValueById}
      getBorrowedLayout={getBorrowedLayout}
      findFieldByRole={findFieldByRole}
      renderRevealSection={renderRevealSection}
      findUniquenessConflictMessage={findUniquenessConflictMessage}
      valueKey={valueKey}
      getConditionValue={getConditionValue}
    />
  );
}

/**
 * Everything a Date field's rules need from OUTSIDE the field: the value and label of the field
 * named by `mustFollowFieldId`, resolved for this repeatable instance. Built once per render and
 * shared by the blur check and the picker's disabled days.
 */
function dateRuleContextFor(
  field: WorkflowField,
  language: 'en' | 'fr',
  getFieldById: ((id: string) => WorkflowField | undefined) | undefined,
  resolveValue: ((id: string) => FieldValue) | undefined,
  fallbackLabel: string,
): DateRuleContext {
  const refId = field.type === 'Date' ? field.mustFollowFieldId : null;
  if (!refId) return {};
  const refField = getFieldById?.(refId);
  const label = refField ? labelFor(refField, language).replace(/\*\*/g, '').trim() : '';
  return {
    mustFollowValue: resolveValue?.(refId),
    mustFollowLabel: label || fallbackLabel,
  };
}

/**
 * Wraps the label + control + hint stack with on-blur semantic validation.
 * Only ShortText carries a `validate` rule today, but the wrapper is shared
 * for all inputs so adding a future rule on another type is a one-line
 * extension. Error state is per-field instance and cleared the moment the
 * advisor types again — once they see the message and act on it, they
 * shouldn't have to keep staring at red.
 */
function ValidatedInputField({
  field: authoredField,
  value,
  onChange,
  language,
  mode,
  readOnly,
  addressAutocomplete,
  getFieldById,
  getValueById,
  setValueById,
  getBorrowedLayout,
  findFieldByRole,
  renderRevealSection,
  findUniquenessConflictMessage,
  valueKey,
  getConditionValue,
}: {
  field: WorkflowField;
  value: FieldValue;
  onChange: (next: FieldValue) => void;
  language: 'en' | 'fr';
  mode?: 'preview' | 'advisor';
  /** See {@link Props.readOnly}. */
  readOnly?: boolean;
  /** See {@link Props.addressAutocomplete}. */
  addressAutocomplete?: boolean;
  getFieldById?: (id: string) => WorkflowField | undefined;
  getValueById?: (id: string) => FieldValue;
  setValueById?: (id: string, next: FieldValue) => void;
  getBorrowedLayout?: (id: string) => { columnCount: number; columnIndex: number } | undefined;
  findFieldByRole?: (role: AddressRole) => WorkflowField | undefined;
  /** Section-reveal renderer — see Props on FieldRenderer. */
  renderRevealSection?: (sectionId: string, inheritedDisabled?: boolean) => ReactNode;
  findUniquenessConflictMessage?: (
    field: WorkflowField,
    value: FieldValue,
    ownValueKey?: string,
  ) => string | null;
  valueKey?: string;
  /** See {@link Props.getConditionValue}. */
  getConditionValue?: (id: string) => FieldValue;
}) {
  const { t, i18n } = useTranslation();
  // The field as the CLIENT's language offers it: an option scoped to the other language is
  // simply not there (QA D-30 — no "Ms." for a French client). Everything below — every option
  // control, the card layout, the reveal slots — reads this narrowed copy. FormShell separately
  // clears an answer that stops being offered, so nothing here has to reconcile a stale value.
  const clientLanguage = useContext(ClientLanguageContext);
  const dateFormat = useContext(DateFormatContext);
  const field = useMemo(
    () => withOfferedOptions(authoredField, clientLanguage),
    [authoredField, clientLanguage],
  );
  const dateRuleContext = dateRuleContextFor(
    field,
    language,
    getFieldById,
    getConditionValue ?? getValueById,
    t('formRenderer.validation.date.mustFollowFallbackField'),
  );
  // Either an i18n key (validate-rule errors) or a resolved string (custom
  // required-empty copy from the originator). Storing the resolved text
  // keeps the renderer one-shot — `t()` runs once at the moment we decide
  // there's an error, not on every render.
  const [errorText, setErrorText] = useState<string | null>(null);
  // The last typed date the picker could not keep (13/45/1990): the picker leaves the value empty,
  // so a later check — Next, a language switch — would otherwise call it "required" (QA N6 /
  // RDSP-VAL-01). Forgotten as soon as the value changes.
  const unkeptTypedDateRef = useRef<string | null>(null);
  // Has this field ever run its blur checks? Read by the ZeroBounce effect below, which is the
  // one check that can fire without the person having done anything, and so is the one that
  // needs to know whether they have.
  const blurredOnceRef = useRef(false);
  // The value as this field last knows it — the prop at render time, or whatever handleChange
  // just committed. The `value` PROP is one render behind any control that commits a value and
  // blurs in the SAME gesture: the date picker blurs its input the instant a calendar day closes
  // the panel, so the blur check ran against the empty prop and answered "This field is required"
  // about the date the advisor had just picked. Read this, never the prop, inside handleBlur.
  const latestValueRef = useRef<FieldValue>(value);
  latestValueRef.current = value;
  const hint = hintFor(field, language);
  // One id family per rendered field. `useId` is stable across renders and
  // unique per instance, which matters here because a repeatable section
  // mounts the SAME authored field many times — hand-rolled ids off field.id
  // would collide across instances and point every error at the first one.
  const uid = useId();
  const controlId = `${uid}ctl`;
  const labelId = `${uid}lbl`;
  const errorId = `${uid}err`;
  const blockId = `${uid}blk`;
  const hintId = `${uid}hnt`;
  // Only consulted by the rep-code exception in handleBlur. Module-cached by
  // useSession(), so this costs nothing per field.
  const { user: blurSessionUser } = useSession();
  // ZeroBounce, for a `validate: 'email'` field. Null when this runtime may not verify (the
  // public customer), which is the only check any leaf needs to make — see emailVerifyContext.
  const emailVerify = useEmailVerify();
  // This field's current verdict, recomputed whenever the store changes. Kept as a render
  // value rather than read inside handleBlur alone, because the answer arrives AFTER the
  // person has already tabbed away: the effect below is what puts the message on screen then.
  const emailVerdict =
    field.validate === 'email' && typeof value === 'string'
      ? emailValueVerdict(value, emailVerify?.lookup)
      : null;

  // `typedDate` is the Date control's raw input text at blur — the only place a typed date the
  // picker is about to discard can still be seen. Other controls pass their DOM event through
  // this same prop, hence the string check rather than trusting the parameter.
  /** The message for a broken rule: this field's authored wording, else the generic copy. */
  const message = (errorKey: string, params?: Record<string, string | number>) =>
    resolveValidationMessage(field, errorKey, params, language, t);
  /** Write a value upward AND to {@link latestValueRef}, so a check in the same tick sees it. */
  const commit = (next: FieldValue) => {
    latestValueRef.current = next;
    onChange(next);
  };
  const handleBlur = (typedDate?: unknown) => {
    // A locked application is a record to READ, not a form to complete. Validating it would
    // paint every unfilled optional-then-required field red as the advisor tabs through
    // reviewing their own submitted work, against a form they cannot fix. Kept separate from
    // the `field.readOnly` line below, which is about one prefilled field, not the whole shell.
    if (readOnly) return;
    // Read-only fields are populated from prefill (sourceValue) or an
    // originator-supplied default, not from advisor typing. Skip the
    // required-empty check so a temporarily-empty prefilled input doesn't
    // paint red before the session resolver lands its value. The rep-code
    // picker is the exception — nothing will arrive to fill it, the advisor
    // must choose (mirrors FormShell's isRequiredExempt).
    if (field.readOnly && !isAdvisorRepCodeChoice(field, blurSessionUser)) return;
    // Past the two exemptions, so this marks "this field has actually been checked once" —
    // what the ZeroBounce effect below waits for before it is allowed to paint anything.
    blurredOnceRef.current = true;
    // Leading / trailing spaces are not part of an answer (QA AH-007): '  John  ' would otherwise
    // be kept, prefilled onward and printed. Written back only when something changes, and
    // BEFORE the checks below so they judge what is actually kept. Not on a read-only field (the
    // return above), a sin / phone / postal rule (its mask owns the text) or a display mask (the
    // value is digits already). Inner double spaces are the regex rule's business, not this.
    let current = trimOnBlur(field, latestValueRef.current);
    if (current !== latestValueRef.current) commit(current);
    // Required-empty check fires first. When the advisor leaves a required
    // input empty, prefer the originator-authored bilingual message; fall
    // back to a generic "This field is required" copy when unset.
    // A typed date the picker could not keep. Checked BEFORE the required rule, because the
    // picker has already left the value empty and "This field is required" would blame the
    // advisor for leaving blank a field they just typed into.
    const typedText =
      typeof typedDate === 'string'
        ? typedDate
        : field.type === 'Date' && !isInputFilled(current)
          ? unkeptTypedDateRef.current
          : null;
    if (field.type === 'Date' && typeof typedText === 'string' && typedText.trim() !== '') {
      // Keep the date the advisor typed even when the picker refused it, so the Next gate
      // judges it too — see parseTypedDate.
      const typedIso = parseTypedDate(typedText, dateFormat);
      if (typedIso && typedIso !== current) {
        commit(typedIso);
        // The checks below judge the date we just kept, not the empty value the picker left
        // behind — otherwise a date typed straight into the input (never committed by the
        // picker, which only commits on Enter) fell through to the required rule.
        current = typedIso;
      }
      unkeptTypedDateRef.current = typedIso ? null : typedText;
      const typed = validateTypedDate(typedText, field, dateRuleContext, dateFormat);
      if (typed?.errorKey) {
        setErrorText(message(typed.errorKey, typed.params));
        return;
      }
    }
    if (field.required && !isInputFilled(current) && !cellsOwnTheRequiredMessage(field)) {
      setErrorText(message('formRenderer.validation.required'));
      return;
    }
    // Display mask (QA D-21): a value short of the mask's digit count is not a value. Same
    // check FormShell's failsFormatRule makes, so the red text and the held Next agree.
    if (
      field.type === 'ShortText' &&
      field.displayMask &&
      isMaskIncomplete(field.displayMask, current)
    ) {
      setErrorText(message('formRenderer.validation.displayMask'));
      return;
    }
    // Date rules (min-age / no-future, no-past, must-follow). The picker greys
    // out-of-range days, but AntD accepts typed input, and a must-follow date
    // can fall out of range when the date it follows changes afterwards — so
    // the committed value itself has to be checked.
    if (field.type === 'Date') {
      const dateResult = validateDateRules(
        field,
        typeof current === 'string' ? current : null,
        dateRuleContext,
      );
      if (dateResult?.errorKey) {
        setErrorText(message(dateResult.errorKey, dateResult.params));
        return;
      }
    }
    if (field.validate) {
      const result = runValidate(
        field.validate as ValidateRule,
        typeof current === 'string' ? current : '',
        field.regexPattern,
      );
      if (result?.errorKey) {
        // `params` carries per-value detail (the offending postal-code letters).
        setErrorText(message(result.errorKey, result.params));
        return;
      }
    }
    // Authored min / max length. The DOM maxlength only stops keystrokes — a paste or a scripted
    // fill walks past it — and minLength had no runtime check at all. Same helper as FormShell's
    // failsFormatRule, so the red text and the held Next agree.
    const tooShortOrLong = fieldLengthViolation(field, current);
    if (tooShortOrLong) {
      setErrorText(message(tooShortOrLong.errorKey, tooShortOrLong.params));
      return;
    }
    // Authored Minimum / Maximum on a Number field. The control caps how WIDE the figure may
    // be, not how large — a max of 123 still admits 800 — so the bound itself is named here.
    // Same helper as FormShell's failsNumberRange, so the red text and the held Next agree.
    const outOfRange = fieldNumberRangeViolation(field, current);
    if (outOfRange) {
      setErrorText(message(outOfRange.errorKey, outOfRange.params));
      return;
    }
    // Cross-field uniqueness — check against peers tagged with the same
    // uniquenessGroup once the semantic validation above has passed.
    // The message arrives fully resolved — FormShell owns the wording because
    // it is the only layer that can name the peer's STEP as well as its label.
    if (hasUniquenessGroup(field) && findUniquenessConflictMessage) {
      const conflict = findUniquenessConflictMessage(field, current, valueKey);
      if (conflict !== null) {
        setErrorText(conflict);
        return;
      }
    }
    // ZeroBounce, last of all: every cheaper rule has had its say, so we only spend a metered
    // credit on a value nothing local objects to, and no local message can be overwritten by a
    // verdict that lands later.
    if (field.validate === 'email' && typeof current === 'string') {
      // Idempotent per address for the life of the shell — safe to call from here even though
      // this same handler re-runs when the verdict arrives (see useEmailVerification).
      emailVerify?.request(current);
      const verdict = emailValueVerdict(current, emailVerify?.lookup);
      if (verdict === 'invalid') {
        const suggestion = emailVerify?.lookup(normalizeEmail(current)).result?.didYouMean;
        const text = message('formRenderer.validation.email.notDeliverable');
        setErrorText(
          suggestion
            ? `${text} ${t('formRenderer.validation.email.didYouMean', { suggestion })}`
            : text,
        );
        return;
      }
      // `checking` deliberately shows NOTHING. It lasts a few hundred milliseconds and is not
      // the person's problem to solve, so painting the field red for it would be a message
      // that appears and vanishes before it can be read. Next is still held for that window —
      // FormShell reads the same verdict through `failsFormatRule` — which is the part that
      // actually matters: it closes the gap a fast clicker could otherwise walk through.
    }
    setErrorText(null);
  };
  // Next pressed on an unfinished step: check this field now, as if the advisor had left it.
  // Through a ref so the effect fires on the signal alone, not on every render's new closure.
  //
  // A field the CONDITIONS were hiding is not merely invisible — it is not mounted at all, so the
  // Next that raised the signal never asked it for anything. Revealing it afterwards (the NonReg
  // "Add another Joint holder → yes" gate opens six required fields at once) used to mount it
  // straight into the raised tick: every one of them was red before the advisor could reach the
  // first. Capture the tick this field was born under and answer only to a tick that moves PAST
  // it — the same rule a repeatable entry follows (see FreshEntryErrorScope), applied per field
  // because a revealed field has no entry of its own to be scoped by.
  //
  // So: a field on screen when Next was pressed shows its errors, unchanged; one revealed
  // afterwards starts clean and is named by the following Next. Display only — FormShell's
  // completeness gate is untouched, so a blank revealed field still holds Next.
  const raisedTick = useContext(ShowErrorsContext);
  const [bornTick] = useState(raisedTick);
  const showErrorsTick = raisedTick > bornTick ? raisedTick : 0;
  const handleBlurRef = useRef(handleBlur);
  handleBlurRef.current = handleBlur;
  useEffect(() => {
    if (showErrorsTick > 0) handleBlurRef.current();
  }, [showErrorsTick]);
  // A message on screen is stored already translated, so switching EN/FR left it in the old
  // language until Next was pressed again (QA N1 / RDSP-UI-02). Re-check the field instead: the
  // same checks, in the new language.
  const errorShownRef = useRef(false);
  errorShownRef.current = errorText !== null;
  useEffect(() => {
    if (errorShownRef.current) handleBlurRef.current();
  }, [language, i18n.language]);
  // A value written by something OTHER than this field's own control: Canada Post AddressComplete
  // cross-filling City / Province / Postal / Apartment through setValueById, a late prefill, a
  // conditional default. Only handleChange clears the red text, and none of those go through it —
  // so after a failed Next the siblings AddressComplete had just filled still read "This field is
  // required". Re-run the same check instead of blindly clearing: a value that is now fine loses
  // its message, one that is still wrong re-words itself. Gated on a message being on screen so
  // an untouched field is never painted red by a value arriving from elsewhere.
  useEffect(() => {
    if (errorShownRef.current) handleBlurRef.current();
  }, [value]);
  // A ZeroBounce verdict landing for this field's value. Unlike every effect above, this one
  // is NOT gated on a message already being on screen: the whole point is the case where the
  // field looked clean when the person tabbed out of it and the answer says otherwise a few
  // hundred milliseconds later. Gated on having been blurred at least once instead — a field
  // nobody has touched yet should not go red on its own.
  //
  // Re-running the full chain rather than setting the message directly is the same choice the
  // three effects above make, and for the same reason: a value that is now fine loses its
  // message and one that is still wrong re-words itself, with no second copy of the rule order
  // to keep in step. It is safe to re-enter because `request` is idempotent per address.
  useEffect(() => {
    if (blurredOnceRef.current) handleBlurRef.current();
  }, [emailVerdict]);
  // Wrap onChange so the error clears the moment the advisor edits — keeps
  // the message from lingering after they've started fixing it.
  const handleChange = (next: FieldValue) => {
    unkeptTypedDateRef.current = null;
    if (errorText) setErrorText(null);
    commit(next);
  };
  // Checkbox + Toggle render the label INLINE with the control (the tick-box
  // sits next to the text). Skipping the above-control FieldLabel avoids
  // rendering the same text twice. Required-flag indicators are baked into
  // the inline label via labelFor() upstream.
  // Originator-driven `hideLabel` flag also short-circuits the field label
  // for cases where a preceding StaticText already explains the input and
  // a repeat label would just add noise.
  const inlineLabelType = field.type === 'Checkbox' || field.type === 'Toggle';
  const skipLabel = inlineLabelType || field.hideLabel === true;
  // Block-continue feedback — when the advisor's answer matches the field's
  // blocking gate, surface the originator-authored explanation as red error
  // text under the input (FormShell separately disables Next). When the
  // originator also flipped the alert switch, pop a modal with the same
  // copy; the modal is mounted only while a blocking answer is selected,
  // so changing away and re-selecting remounts it → it re-pops every time.
  const blocked = isBlockingValueMatch(field, value);
  // Next stays enabled on a blocking answer (QA RIF C7): with no authored explanation, pressing it
  // still has to say why under the field, so the generic one shows once Next has been tried.
  const blockMessage = blocked
    ? (blockContinueMessageFor(field, language) ??
      (showErrorsTick > 0
        ? t('formRenderer.nextBlockedByAnswer', { field: labelFor(field, language) })
        : null))
    : null;
  // WCAG 2.1 field contract. Every input needs a programmatic name (1.3.1,
  // 3.3.2), its validity exposed (4.1.2), and its error/hint text POINTED AT
  // rather than merely sitting nearby (3.3.1) — visual adjacency means nothing
  // to a screen reader. `describedBy` lists only what is actually on screen, so
  // it never dangles at an id that isn't rendered.
  const kind = a11yKindFor(field.type);
  const describedBy =
    [errorText ? errorId : null, blockMessage ? blockId : null, hint ? hintId : null]
      .filter(Boolean)
      .join(' ') || undefined;
  const a11y: FieldA11y = {
    controlId,
    labelId,
    describedBy,
    required: field.required === true,
    invalid: errorText !== null,
    labelText: labelFor(field, language),
  };
  const control = (
    <FieldControl
      field={field}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      language={language}
      invalid={errorText !== null}
      mode={mode}
      readOnly={readOnly}
      addressAutocomplete={addressAutocomplete}
      getFieldById={getFieldById}
      getValueById={getValueById}
      setValueById={setValueById}
      getBorrowedLayout={getBorrowedLayout}
      findFieldByRole={findFieldByRole}
      renderRevealSection={renderRevealSection}
      findUniquenessConflictMessage={findUniquenessConflictMessage}
      a11y={a11y}
      dateRuleContext={dateRuleContext}
      getConditionValue={getConditionValue}
      onRestrictionRefused={(restriction) =>
        setErrorText(
          t(`formRenderer.validation.restricted.${restriction}`, {
            defaultValue: t('formRenderer.validation.restricted.other'),
          }),
        )
      }
    />
  );
  return (
    <div style={{ marginBottom: 16 }} data-blocked-field={blocked ? field.id : undefined}>
      {!skipLabel && (
        <FieldLabel
          field={field}
          language={language}
          id={labelId}
          // Only a single labellable control can be the target of `htmlFor`.
          // Groups are named via aria-labelledby on the wrapper below.
          htmlFor={kind === 'labelled' ? controlId : undefined}
        />
      )}
      {kind === 'group' ? (
        // Several controls, one question: name the CONTAINER. Without this a
        // screen reader reads the options with no idea what is being asked.
        <div role="group" aria-labelledby={labelId} aria-describedby={describedBy}>
          {control}
        </div>
      ) : (
        control
      )}
      {/* `role="alert"` so validation failures are ANNOUNCED, not just drawn
          (4.1.3). The id is what `aria-describedby` above points at, which is
          what associates the message with the input it belongs to (3.3.1). */}
      {errorText && (
        <div id={errorId} role="alert" style={{ marginTop: 4, fontSize: 12, color: '#dc2626' }}>
          {errorText}
        </div>
      )}
      {blockMessage && (
        <div id={blockId} role="alert" style={{ marginTop: 4, fontSize: 12, color: '#dc2626' }}>
          {blockMessage}
        </div>
      )}
      {/* The hint is SUPPRESSED visually while an error or block message shows —
          the original design choice, kept. It stays mounted (visually hidden)
          rather than unmounted so `aria-describedby` still resolves: the
          instructions matter most at the moment validation fails, and dropping
          them from the accessibility tree exactly then is backwards. */}
      {hint && (errorText || blockMessage) && (
        <span id={hintId} className="sr-only">
          {hint}
        </span>
      )}
      {hint && !errorText && !blockMessage && (
        // Rich text, not a plain string: hints carry guidance that often wants
        // a link ("attach the transfer form", "see the fund codes PDF") or
        // emphasis, and as a bare string a markdown link rendered as literal
        // `[text](url)`. `inline` drops images and the paragraph wrapper so a
        // one-line hint keeps sitting tight under its input.
        //
        // Safe for existing hints: plain prose renders identically through the
        // markdown path, and every hint in the seeds today is plain prose.
        <RichText
          id={hintId}
          body={hint}
          variant="inline"
          style={{ display: 'block', marginTop: 6, fontSize: 12, color: '#6b7280' }}
        />
      )}
      {blocked && field.blockContinueAlert === true && (
        <BlockContinueAlertModal
          restart={field.blockContinueRestart === true}
          body={
            blockMessage ||
            t('formRenderer.nextBlockedByAnswer', { field: labelFor(field, language) })
          }
        />
      )}
    </div>
  );
}

/** Originator-authored block-continue explanation, FR falling back to EN
 *  (same convention as hints / required messages). Empty string → null so
 *  callers can gate rendering on truthiness. */
function blockContinueMessageFor(field: WorkflowField, language: 'en' | 'fr'): string | null {
  const authored =
    language === 'fr' && field.blockContinueMessageFr?.trim()
      ? field.blockContinueMessageFr
      : field.blockContinueMessageEn;
  return authored?.trim() ? authored.trim() : null;
}

/** Alert modal popped when the advisor selects a blocking answer on a field
 *  whose originator enabled the block-continue alert switch. Unlike
 *  NoticeModal there is no session-level dismissed set — the caller mounts
 *  this only while the blocking answer is selected, so re-selecting the
 *  answer remounts the component and the modal re-pops (deliberate: it's an
 *  error state the advisor keeps re-entering, not a one-time notice). */
function BlockContinueAlertModal({ body, restart }: { body: string; restart: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const restartFromBlock = useRestartFromBlock();
  return (
    <Modal
      open={open}
      onCancel={() => {
        setOpen(false);
        // `blockContinueRestart`: closing starts the application over at Welcome.
        if (restart) restartFromBlock?.();
      }}
      footer={null}
      width={480}
      closable
      // Using the Modal's own title slot puts the icon + heading on the same
      // chrome row as the built-in close X — AntD vertically centers them
      // for us, so no hand-rolled alignment.
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <WarningOutlined style={{ color: '#dc2626', fontSize: 20 }} />
          {t('formRenderer.blockContinueTitle')}
        </span>
      }
    >
      <div style={{ lineHeight: 1.5, color: '#1f2937', paddingTop: 4 }}>{body}</div>
    </Modal>
  );
}

/**
 * True when this field's own "This field is required." would only repeat what its
 * cells already say — a required RepeatableRow whose template carries at least one
 * required column.
 *
 * The table always renders a row (RepeatableRowControl falls back to one empty
 * placeholder), and `field.required` makes every rendered row one that Next holds,
 * so each blank required cell names itself under its own input once Next is pressed.
 * The field-level message is drawn AFTER the control — i.e. under "ADD ANOTHER FUND
 * (MAXIMUM 5)" — where it reads as a demand to add a row the advisor can see is
 * already on screen, next to three cells that have just said the real thing.
 *
 * A template with no required column has no per-cell message to defer to: there
 * "required" means "put something in this table", nothing else says it, and the
 * field-level message stands. Display only — FormShell's Next gate is untouched,
 * so an untouched required table still holds the step either way.
 */
function cellsOwnTheRequiredMessage(field: WorkflowField): boolean {
  return (
    field.type === 'RepeatableRow' &&
    (field.repeatableTemplate ?? []).some((col) => col.required === true)
  );
}

/** "Has the advisor filled this input?" mirrors FormShell.isFilled at the
 *  per-field scope. Kept in sync deliberately so the inline error and the
 *  Next-button block agree — a required RepeatableRow with an untouched
 *  placeholder row (`[{col_a: null}]`) reads as unfilled both places.
 *  Can't share the FormShell helper directly without a circular import. */
function isInputFilled(v: FieldValue): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v === 'boolean') return v;
  if (Array.isArray(v)) {
    if (v.length === 0) return false;
    // RepeatableRow value — array of objects. Treat all-null cells as unfilled
    // (matches FormShell's isFilled). CheckboxGroup string[] falls through as
    // filled when non-empty.
    const probe = (v as unknown[]).find((x) => x !== null && x !== undefined);
    if (typeof probe === 'object' && probe !== null) {
      return (v as Array<Record<string, unknown> | null>).some(
        (row) =>
          row !== null &&
          Object.values(row).some(
            (cell) =>
              cell !== null &&
              cell !== undefined &&
              cell !== '' &&
              (typeof cell !== 'number' || Number.isFinite(cell)),
          ),
      );
    }
    return true;
  }
  return true;
}

function contentFor(field: WorkflowField, language: 'en' | 'fr'): string {
  if (language === 'fr' && field.labelFr) return field.labelFr;
  return field.labelEn;
}

function labelFor(field: WorkflowField, language: 'en' | 'fr'): string {
  if (language === 'fr' && field.labelFr) return field.labelFr;
  return field.labelEn;
}

function hintFor(field: WorkflowField, language: 'en' | 'fr'): string {
  if (language === 'fr' && field.hintFr) return field.hintFr;
  return field.hintEn ?? '';
}

/** Per-field style for StaticText / Paragraph blocks. Returns an empty object
 *  for input types so the caller can spread it unconditionally. Margins default
 *  to 20 px on both sides when the originator hasn't configured a value — gives
 *  static copy enough breathing room without requiring per-field setup. */
// Named font-weight buckets the FE Select exposes -> CSS weight numbers.
// Kept tiny + at module scope so it doesn't reallocate per render. Trimmed
// to three values — medium / light / semibold dropped because the visual
// delta vs. regular is too subtle in most fonts to deserve a separate slot.
const FONT_WEIGHT_PX: Record<string, number> = {
  thin: 100,
  regular: 400,
  bold: 700,
};

/**
 * Project-wide rich-text renderer. Accepts a plain string with markdown:
 *   - `[label](url)` → external link (new tab + noopener)
 *   - `**bold**` / `*italic*` → emphasis
 *   - `![alt](url)` → embedded image (only the `block` variant — turned off in
 *     `inline` so a stray `!` in a Notice doesn't accidentally render).
 *   - Plain newline → real `<br>` via the `remark-breaks` plugin so originators
 *     can press Enter for line breaks without typing the markdown "two spaces"
 *     hack. Used everywhere the project shows rich text (help body, Notice,
 *     Paragraph, Static text).
 *
 * The `style` prop is spread on the wrapper so callers that already own the
 * typography (StaticText, Paragraph, Notice) can pass through their fontSize /
 * color / fontWeight / margins without forking the renderer.
 */
export function RichText({
  body,
  variant = 'block',
  style,
  id,
}: {
  body: string;
  /** Set when the text is the target of an `aria-describedby` / `aria-labelledby`
   *  pointer, so the association resolves to a real element. */
  id?: string;
  /** `block` keeps full markdown (paragraphs, images, line breaks). `inline`
   *  drops images and the paragraph wrapper — used inside a single-line
   *  container like a Notice where we don't want browser-default `<p>` margin
   *  collapsing into the callout padding. */
  variant?: 'block' | 'inline';
  style?: React.CSSProperties;
}) {
  // Module-scoped component maps below — passing a stable reference into
  // ReactMarkdown lets it skip re-resolving component overrides on every
  // parent re-render. Earlier per-call `useRichTextComponents()` returned a
  // fresh object literal, forcing react-markdown to rebuild its node renderer
  // on every Notice/Paragraph re-render of the form.
  const components = variant === 'inline' ? INLINE_MARKDOWN_COMPONENTS : BLOCK_MARKDOWN_COMPONENTS;
  // Inline wraps in a <span>, not a <div>: field labels render markdown inside
  // an existing inline run (next to the required asterisk, inside the
  // checkbox's <span> label slot), where a block wrapper would push the
  // asterisk to its own line and nest a <div> inside a <span>. StaticText —
  // the other inline caller — already passes `display: 'block'` explicitly,
  // so it keeps its current layout.
  const Wrapper = variant === 'inline' ? 'span' : 'div';
  return (
    <Wrapper id={id} className="bmo-richtext" style={{ lineHeight: 1.55, ...style }}>
      <ReactMarkdown remarkPlugins={[remarkBreaks]} components={components}>
        {body}
      </ReactMarkdown>
    </Wrapper>
  );
}

/** Back-compat alias — keeps existing call sites in the help modal working
 *  while the rest of the renderer adopts the new name. Old `HelpBodyMarkdown`
 *  was inline-style fixed; new behaviour adds remark-breaks. */
export function HelpBodyMarkdown({ body }: { body: string }) {
  return <RichText body={body} variant="block" style={{ fontSize: 14, color: '#111827' }} />;
}

// react-markdown component overrides shared by every RichText caller. Hoisted
// to module scope so the same object reference flows in on every render —
// critical because react-markdown short-circuits when `components` is
// reference-equal. Splitting into block/inline avoids a runtime branch per
// element type.

/** Anchor renderer common to both variants — opens in a new tab with
 *  rel="noopener noreferrer" to prevent tabnabbing on `[label](url)` links. */
const MarkdownAnchor = ({ node, ...props }: { node?: unknown }) => {
  void node;
  // Underlined per the 2026-07-24 BMO design, and because colour alone is not
  // a sufficient affordance for a link (WCAG 1.4.1 — use of colour).
  return (
    // react-markdown passes the link text through `props.children`, so the
    // anchor always has content at runtime — the rule just can't see through
    // the spread.
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'underline' }}
    />
  );
};

const MarkdownImage = ({ node, ...props }: { node?: unknown; alt?: string }) => {
  void node;
  return (
    <img
      {...props}
      style={{
        display: 'block',
        maxWidth: '100%',
        height: 'auto',
        borderRadius: 6,
        // Comfortable breathing room between the prose and an attached image.
        marginTop: 16,
        marginBottom: 8,
      }}
      alt={props.alt ?? ''}
    />
  );
};

/** `<p>` in block markdown. Margins are owned by the `.bmo-richtext` rules in
 *  global.css rather than an inline `margin: 0` here — the inline style won
 *  against every stylesheet, which also flattened the gap BETWEEN paragraphs,
 *  so a two-paragraph help body ran together as one block. The CSS keeps the
 *  outer margins at 0 (so the wrapper's marginTop / marginBottom still owns
 *  layout, as before) while giving `p + p` real separation. */
const MarkdownBlockParagraph = ({ node, ...props }: { node?: unknown }) => {
  void node;
  return <p {...props} />;
};

/** `<p>` collapsed to `<span>` in inline contexts (Notice, StaticText) where
 *  a block paragraph would break flex layout or collapse with surrounding
 *  padding. */
const MarkdownInlineParagraph = ({ node, ...props }: { node?: unknown }) => {
  void node;
  return <span {...props} />;
};

const BLOCK_MARKDOWN_COMPONENTS = {
  a: MarkdownAnchor,
  img: MarkdownImage,
  p: MarkdownBlockParagraph,
} as const;

const INLINE_MARKDOWN_COMPONENTS = {
  a: MarkdownAnchor,
  // Inline contexts drop images entirely — a Notice / one-line StaticText
  // shouldn't grow into an image container.
  img: () => null,
  p: MarkdownInlineParagraph,
} as const;

/** Per-keystroke filter for ShortText / LongText when the originator set an
 *  InputRestriction. Strips characters that don't match the selected class
 *  before the value reaches the form state, so the advisor never sees a
 *  character they can't keep.
 *
 *  `letters` is the canonical name-input filter: Unicode \p{L} covers French
 *  accents (é è à ç …) and \p{M} catches combining marks for decomposed
 *  Unicode forms. Apostrophes (ASCII `'` and curly `’`), hyphens, and spaces
 *  are also passed so Canadian names like O'Brien, Saint-Jean, and Jean
 *  Pierre work without forcing the advisor to strip them manually. */
function applyInputRestriction(restriction: string | null | undefined, value: string): string {
  if (!restriction) return value;
  if (restriction === 'digits') return value.replace(/[^0-9]/g, '');
  if (restriction === 'letters') {
    // Two passes:
    //  (1) keep only letters, combining marks, apostrophes (ASCII + curly),
    //      hyphens, and spaces;
    //  (2) drop combining marks that aren't attached to a base letter — the
    //      previous one-pass version let orphan diacritics through (paste
    //      U+0301 alone and it survived as a free-floating accent).
    const kept = value.replace(/[^\p{L}\p{M}'’\- ]/gu, '');
    return kept.replace(/(^|[^\p{L}\p{M}])(\p{M}+)/gu, '$1');
  }
  if (restriction === 'alphanumeric') return value.replace(/[^\p{L}\p{N}]/gu, '');
  return value;
}

function textStyleFor(field: WorkflowField): React.CSSProperties {
  if (field.type !== 'StaticText' && field.type !== 'Paragraph') return {};
  const out: React.CSSProperties = {
    marginTop: field.marginTop ?? DEFAULT_TEXT_MARGIN_PX,
    marginBottom: field.marginBottom ?? DEFAULT_TEXT_MARGIN_PX,
  };
  if (field.fontSize) out.fontSize = field.fontSize;
  if (field.color) out.color = field.color;
  if (field.fontWeight && FONT_WEIGHT_PX[field.fontWeight]) {
    out.fontWeight = FONT_WEIGHT_PX[field.fontWeight];
  }
  return out;
}

/**
 * Renders a BulletedList field at runtime. Disc / decimal use native
 * <ul>/<ol> markers (browser-drawn bullets and numbers). Dash and checkmark
 * are custom-rendered with `list-style: none` + a leading marker span, since
 * CSS list-style-type doesn't expose those glyphs natively.
 */
function BulletedListRenderer({
  field,
  language,
}: {
  field: WorkflowField;
  language: 'en' | 'fr';
}) {
  // One modal for whichever item the advisor opened. Storing the option's
  // `value` (not the index) means re-renders driven by upstream state can't
  // misalign the open item with its content.
  const [openItem, setOpenItem] = useState<string | null>(null);

  // Small marginTop so the list sits close to whatever precedes it (typically
  // a StaticText heading the originator authored above). Bottom margin keeps
  // the default 20-px breathing room before the next block.
  const style = {
    marginTop: 4,
    marginBottom: DEFAULT_TEXT_MARGIN_PX,
    paddingLeft: 24,
  } as const;
  // Per-<li> bottom spacing for the disc / decimal branches — gives the
  // bullets some breathing room without doubling up on the dash/checkmark
  // branch's gap (it already sets its own marginBottom per item).
  const itemStyle = { marginBottom: 8 } as const;
  const text = (o: WorkflowField['options'][number]) =>
    language === 'fr' && o.labelFr ? o.labelFr : o.labelEn;
  // The text is what matters; the title is optional — a bubble may be just the text (Martha, Reg/Non-Reg
  // eligibility: the residency note without a heading). An untitled modal renders no header bar.
  const hasHelp = (o: WorkflowField['options'][number]) => Boolean(o.helpBodyEn);
  const helpTitle = (o: WorkflowField['options'][number]) =>
    language === 'fr' && o.helpTitleFr ? o.helpTitleFr : (o.helpTitleEn ?? '');
  const helpBody = (o: WorkflowField['options'][number]) =>
    language === 'fr' && o.helpBodyFr ? o.helpBodyFr : (o.helpBodyEn ?? '');

  // Match the field-level help icon (see FieldLabel): InfoCircleOutlined inside
  // a transparent <button>, 18 px in BMO blue. Same visual everywhere so the
  // advisor reads "ⓘ" as the same affordance regardless of where it appears.
  const HelpIcon = ({ value }: { value: string }) => (
    <button
      type="button"
      onClick={() => setOpenItem(value)}
      aria-label="More info"
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        marginLeft: 6,
        cursor: 'pointer',
        color: '#0179C1',
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 18,
        verticalAlign: 'middle',
      }}
    >
      <InfoCircleOutlined />
    </button>
  );

  const activeOption = openItem ? (field.options.find((o) => o.value === openItem) ?? null) : null;

  const itemHelpModal = activeOption ? (
    <Modal
      open
      className="bmo-help-modal"
      title={helpTitle(activeOption)}
      onCancel={() => setOpenItem(null)}
      footer={null}
      destroyOnClose
    >
      <HelpBodyMarkdown body={helpBody(activeOption)} />
    </Modal>
  ) : null;

  if (field.listStyle === 'decimal') {
    return (
      <>
        <ol style={style}>
          {field.options.map((o) => (
            <li key={o.value} style={itemStyle}>
              {text(o)}
              {hasHelp(o) && <HelpIcon value={o.value} />}
            </li>
          ))}
        </ol>
        {itemHelpModal}
      </>
    );
  }
  if (field.listStyle === 'dash' || field.listStyle === 'checkmark') {
    // CSS list-style-type has no built-in dash or checkmark, so we render
    // each item ourselves with a leading marker. Flex baseline alignment so
    // multi-line items wrap under the text, not under the marker.
    const isCheck = field.listStyle === 'checkmark';
    return (
      <>
        <ul style={{ ...style, listStyle: 'none', paddingLeft: 4 }}>
          {field.options.map((o) => (
            <li
              key={o.value}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 10,
                marginBottom: 6,
              }}
            >
              <span
                aria-hidden
                style={{
                  flexShrink: 0,
                  color: '#0179C1',
                  fontWeight: 600,
                  lineHeight: 1.4,
                }}
              >
                {isCheck ? '✓' : '—'}
              </span>
              <span>
                {text(o)}
                {hasHelp(o) && <HelpIcon value={o.value} />}
              </span>
            </li>
          ))}
        </ul>
        {itemHelpModal}
      </>
    );
  }
  // disc (default) — native <ul> with browser bullets.
  return (
    <>
      <ul style={style}>
        {field.options.map((o) => (
          <li key={o.value} style={itemStyle}>
            {text(o)}
            {hasHelp(o) && <HelpIcon value={o.value} />}
          </li>
        ))}
      </ul>
      {itemHelpModal}
    </>
  );
}

/** Per-Notice-icon visual treatment. Single source of truth for the four
 *  built-in variants so a new entry only needs to be added once (instead of
 *  three parallel Records that could drift). Background is the default light
 *  tint when the originator hasn't picked a `backgroundColor`; accent is the
 *  saturated colour used on the left-border + leading icon. */
type NoticeVariant = {
  bg: string;
  accent: string;
  Icon: (props: { style?: React.CSSProperties }) => React.ReactElement;
};
const NOTICE_VARIANTS: Record<string, NoticeVariant> = {
  info: {
    bg: '#EFF6FF',
    accent: '#0179C1',
    Icon: (props) => <InfoCircleOutlined {...props} />,
  },
  warning: {
    bg: '#FFFBEB',
    accent: '#B45309',
    Icon: (props) => <WarningOutlined {...props} />,
  },
  success: {
    bg: '#ECFDF5',
    accent: '#047857',
    Icon: (props) => <CheckCircleOutlined {...props} />,
  },
  tip: {
    bg: '#FAF5FF',
    accent: '#7C3AED',
    Icon: (props) => <BulbOutlined {...props} />,
  },
};
const NOTICE_VARIANT_INFO = NOTICE_VARIANTS.info!;

/**
 * Renders an Image (static layout block) at runtime. Honors imageWidthSize
 * (small/medium/full as a % of the column), imageAlignment (left/center/right),
 * and a bilingual caption beneath the image. Alt text falls back to the
 * caption when not authored, then to an empty string.
 *
 * When no image is uploaded yet, the originator (in preview) sees a dashed
 * placeholder so they know where to upload; the live advisor surface still
 * renders the empty container so the layout doesn't shift if the originator
 * later pushes a published version with the image attached.
 */
function ImageRenderer({ field, language }: { field: WorkflowField; language: 'en' | 'fr' }) {
  const url = field.imageUrl?.trim();
  const caption = language === 'fr' && field.captionFr ? field.captionFr : field.captionEn;
  const altRaw = language === 'fr' && field.altFr ? field.altFr : field.altEn;
  const alt = altRaw?.trim() || caption?.trim() || '';
  const widthPct = WIDTH_PCT_BY_SIZE[field.imageWidthSize ?? 'full'] ?? '100%';
  const justify = JUSTIFY_BY_ALIGNMENT[field.imageAlignment ?? 'left'] ?? 'flex-start';
  const textAlign =
    field.imageAlignment === 'center'
      ? 'center'
      : field.imageAlignment === 'right'
        ? 'right'
        : 'left';

  // Inner block sizes to the image's natural width (so a 200 px source isn't
  // stretched to 100% of a 600 px column), but caps at the chosen width
  // preset. The caption sits inside this block so it tracks the rendered
  // image width, not the column width. For the empty-state placeholder we
  // fall back to a fixed widthPct since there's no natural size to honor.
  const innerStyle: React.CSSProperties = url
    ? { display: 'inline-block', maxWidth: widthPct, width: 'fit-content' }
    : { display: 'block', width: widthPct };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: justify,
        marginTop: field.marginTop ?? DEFAULT_TEXT_MARGIN_PX,
        marginBottom: field.marginBottom ?? DEFAULT_TEXT_MARGIN_PX,
      }}
    >
      <div style={innerStyle}>
        {url ? (
          <img
            src={url}
            alt={alt}
            style={{
              width: 'auto',
              height: 'auto',
              maxWidth: '100%',
              display: 'block',
              borderRadius: 4,
            }}
          />
        ) : (
          <div
            aria-label={alt || 'Image placeholder'}
            style={{
              minHeight: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed #cbd5e1',
              borderRadius: 4,
              color: '#6b7280',
              fontSize: 12,
              background: '#fafafa',
            }}
          >
            No image
          </div>
        )}
        {caption?.trim() && (
          <div
            style={{
              marginTop: 6,
              fontSize: 12,
              color: '#64748b',
              textAlign,
            }}
          >
            {caption}
          </div>
        )}
      </div>
    </div>
  );
}

const WIDTH_PCT_BY_SIZE: Record<string, string> = {
  small: '33%',
  medium: '66%',
  full: '100%',
};
const JUSTIFY_BY_ALIGNMENT: Record<string, 'flex-start' | 'center' | 'flex-end'> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

/**
 * Renders a Notice (tinted callout) at runtime. Honors `backgroundColor`
 * (explicit hex from the originator) and `icon` (info / warning / success /
 * tip). Text uses the field's `color` if set, else dark slate on the tinted
 * default bg.
 */
function NoticeRenderer({ field, language }: { field: WorkflowField; language: 'en' | 'fr' }) {
  const text = language === 'fr' && field.labelFr ? field.labelFr : field.labelEn;
  const iconName = field.icon ?? 'info';
  // Single lookup of the variant treatment — keeps bg / accent / icon in sync
  // when a stale or unknown key arrives (fall back to info, all three).
  const variant = NOTICE_VARIANTS[iconName] ?? NOTICE_VARIANT_INFO;
  const Icon = variant.Icon;
  const bg = field.backgroundColor ?? variant.bg;
  const accent = variant.accent;
  // Text color: explicit override > slate default. The originator can pick
  // #FFFFFF for white text on a saturated bg.
  const color = field.color ?? '#1f2937';
  // Reuse the project-wide name → CSS weight map so 'thin' renders the same
  // (100) on Notice as it does on StaticText / Paragraph. Earlier inline
  // ternary diverged (300 for thin) and silently produced different visuals.
  const fontWeight =
    (field.fontWeight && FONT_WEIGHT_PX[field.fontWeight]) || FONT_WEIGHT_PX.regular!;

  // displayAs === 'modal' — same content, popped as an AntD modal dialog.
  // The visibility condition gates whether THIS component renders at all,
  // so a fresh mount means the trigger just flipped true → open the modal.
  // Local `open` state lets the advisor dismiss via the Close button; the
  // field stays mounted (no re-trigger) until the visibility flips false +
  // back true (which remounts the component, resetting state).
  if (field.displayAs === 'modal') {
    return <NoticeModal noticeId={field.id} text={text} accent={accent} Icon={Icon} />;
  }

  return (
    <div
      role="note"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '12px 14px',
        background: bg,
        // 2026-07-24 BMO design: the accent is a rule UNDER the callout, not a
        // bar down its left edge, and the box is square. (Was a 4px left
        // border + 6px radius, mirroring the AntD Alert pattern.)
        borderBottom: `2px solid ${accent}`,
        borderRadius: 0,
        color,
        fontWeight,
        marginTop: field.marginTop ?? DEFAULT_TEXT_MARGIN_PX,
        marginBottom: field.marginBottom ?? DEFAULT_TEXT_MARGIN_PX,
      }}
    >
      <Icon style={{ color: accent, fontSize: 16, marginTop: 2 }} />
      {/* Block markdown — paragraph breaks render as real paragraph gaps so a
          bold title + body reads on two lines, and `![alt](url)` images can
          embed inside the callout (used by Bulk Request preview etc.). */}
      <RichText
        body={text}
        variant="block"
        style={{ flex: 1, lineHeight: 1.5, color: 'inherit' }}
      />
    </div>
  );
}

/** Session-level Set of Notice field ids the advisor has already dismissed.
 *  Survives FieldRenderer re-mounts (which happen on every Back/Next step
 *  navigation) so the same modal doesn't re-pop when the advisor returns
 *  to a step they already acknowledged. Resets on page reload — that's a
 *  fresh advisor session and seeing the notice again is reasonable. */
const dismissedNoticeIds = new Set<string>();

/** Notice rendered as a modal dialog. Auto-opens on mount unless the
 *  advisor has already dismissed THIS field id this session. Close button
 *  dismisses + records to the session set. Plain modal chrome — the
 *  leading icon is kept (icon variant signals severity); tinted background
 *  + left accent border the inline variant uses are dropped so the modal
 *  reads as a standard dialog rather than a callout-inside-a-dialog. */
function NoticeModal({
  noticeId,
  text,
  accent,
  Icon,
}: {
  noticeId: string;
  text: string;
  accent: string;
  Icon: React.ComponentType<{ style?: React.CSSProperties }>;
}) {
  const [open, setOpen] = useState(() => !dismissedNoticeIds.has(noticeId));
  const handleClose = () => {
    dismissedNoticeIds.add(noticeId);
    setOpen(false);
  };
  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={620}
      // No native title — the markdown body owns its own bold heading so
      // we don't double-stack a title above the body.
      closable
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          padding: 4,
        }}
      >
        <Icon style={{ color: accent, fontSize: 20, marginTop: 4 }} />
        <RichText
          body={text}
          variant="block"
          style={{ flex: 1, lineHeight: 1.5, color: '#1f2937' }}
        />
      </div>
    </Modal>
  );
}

/**
 * How a field type takes its accessible name (WCAG 2.1 — 1.3.1, 3.3.2, 4.1.2).
 *
 *  - `labelled` — one focusable control; the visible label points at it with
 *    `htmlFor`/`id`, which is the strongest and best-supported association.
 *  - `group`    — several controls answering ONE question (radios, checkboxes,
 *    repeatable rows). There is no single element for `htmlFor` to target, so
 *    the container gets `aria-labelledby` instead.
 *  - `inline`   — the control renders its own adjacent label (Checkbox, Toggle),
 *    so the field-level label is suppressed and the name comes from there.
 *  - `block`    — not an input at all; no name to give.
 */
function a11yKindFor(type: WorkflowField['type']): 'labelled' | 'group' | 'inline' | 'block' {
  switch (type) {
    case 'RadioGroup':
    case 'CheckboxGroup':
    case 'RepeatableRow':
      return 'group';
    case 'Checkbox':
    case 'Toggle':
      return 'inline';
    case 'StaticText':
    case 'Paragraph':
    case 'Divider':
    case 'BulletedList':
    case 'Notice':
    case 'Image':
      return 'block';
    default:
      return 'labelled';
  }
}

/**
 * The ids and ARIA a single field needs, derived once per field instance.
 * Bundled rather than passed as loose props because every branch of
 * FieldControl needs some subset, and threading five strings through fifteen
 * cases is how one of them silently ends up missing.
 */
export type FieldA11y = {
  controlId: string;
  labelId: string;
  /** Space-separated ids of whatever descriptive text is currently rendered —
   *  error, block-continue message, hint. Undefined when there is none. */
  describedBy: string | undefined;
  required: boolean;
  invalid: boolean;
  /** Plain-text label, for the few controls that can only take a string name. */
  labelText: string;
};

function FieldLabel({
  field,
  language,
  id,
  htmlFor,
}: {
  field: WorkflowField;
  language: 'en' | 'fr';
  id: string;
  /** Set only for `labelled` fields — a `<label for>` pointing at something
   *  that is not a form control is worse than no label at all. */
  htmlFor?: string;
}) {
  const { t } = useTranslation();
  const [helpOpen, setHelpOpen] = useState(false);
  const helpTitle = language === 'fr' && field.helpTitleFr ? field.helpTitleFr : field.helpTitleEn;
  const helpBody = language === 'fr' && field.helpBodyFr ? field.helpBodyFr : field.helpBodyEn;
  const hasHelp = !!helpTitle && !!helpBody;
  return (
    <>
      {/* `flex` (not inline-flex) so the label is its own row — otherwise the
          control flows alongside the label when the control is also inline
          (e.g. Radio.Group, Checkbox.Group, Switch). */}
      {/* The help button used to live INSIDE this <label>. A button nested in a
          label is invalid, and clicking the label text activated it. Split into
          a flex row so the label stays a pure label. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <label id={id} htmlFor={htmlFor}>
          {/* Regular weight per the 2026-07-24 BMO design — question labels sit
            at the same weight as body copy, with emphasis carried by the
            content beneath (card labels, section headings) rather than the
            label itself. Scoped to the advisor-facing renderer: the
            originator's builder chrome keeps its bold AntD labels via the
            `.ant-form-item-label > label` rule in global.css, which doesn't
            reach this custom label. */}
          <Text style={{ fontSize: 14, fontWeight: 400 }}>
            {/* Markdown, so an author can bold part of a label (`**must**`) the
              way the BMO design does. The `**` convention was already assumed
              downstream — ApplicationReviewStep / FormShell /
              buildApplicationSnapshot all strip the pair before displaying a
              label — but the runtime renderer printed it literally. */}
            <RichText body={labelFor(field, language)} variant="inline" />
            {field.required && <RequiredMark />}
          </Text>
        </label>
        {hasHelp && (
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            aria-label={t('formRenderer.openHelp')}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: '#0179C1',
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: 18,
            }}
          >
            <InfoCircleOutlined />
          </button>
        )}
      </div>
      {hasHelp && (
        <Modal
          open={helpOpen}
          className="bmo-help-modal"
          onCancel={() => setHelpOpen(false)}
          title={helpTitle}
          footer={null}
          width={520}
        >
          <HelpBodyMarkdown body={helpBody!} />
        </Modal>
      )}
    </>
  );
}

/**
 * ZeroBounce's verdict as a glyph on the email input's right edge — the field-level counterpart
 * to the e-signature summary's `EmailVerifyIcon`, which is where the icons and the palette come
 * from, so the two surfaces read as one feature.
 *
 * Only three of the states draw anything; see {@link emailValueIndicator} for why the warn
 * statuses and `Unknown` stay silent here when the signatory roster paints them.
 *
 * The wording lives in the tooltip because the input has no room for a sentence. For `invalid`
 * it is the field's OWN message — the same {@link resolveValidationMessage} call the red text
 * under the input makes — so an originator who authored custom wording for this rule gets it in
 * both places instead of the generic copy in one of them.
 */
function EmailVerifyFieldIcon({
  indicator,
  field,
  language,
}: {
  indicator: EmailFieldIndicator;
  field: WorkflowField;
  language: 'en' | 'fr';
}) {
  const { t } = useTranslation();
  // Nothing to say — but still the box, which is what keeps the suffix container mounted and
  // the input's width stable. See the `suffix` prop for why that matters.
  if (indicator === null) return <span style={EMAIL_GLYPH_BOX} />;
  if (indicator === 'checking') {
    const checkingLabel = t('formRenderer.validation.email.checking');
    return (
      <Tooltip title={checkingLabel}>
        <span style={EMAIL_GLYPH_BOX}>
          {/* `aria-hidden`, unlike the two verdicts below: a spinner is a transient state the
              person did not ask about and cannot act on, and announcing it on every blur would
              interrupt a screen-reader user mid-form for a few hundred milliseconds of nothing.
              The verdict that follows is the part worth hearing. */}
          <LoadingOutlined style={{ color: '#6b7280' }} aria-hidden />
        </span>
      </Tooltip>
    );
  }
  const verified = indicator === 'valid';
  const label = verified
    ? t('formRenderer.validation.email.verified')
    : resolveValidationMessage(
        field,
        'formRenderer.validation.email.notDeliverable',
        undefined,
        language,
        t,
      );
  const Icon = verified ? CheckCircleFilled : ExclamationCircleFilled;
  return (
    <Tooltip title={label}>
      <span style={EMAIL_GLYPH_BOX}>
        {/* Named rather than decorative, because for a sighted user this glyph IS the status and
            the tooltip that explains it is mouse-only. A plain label, not an alert: the invalid
            case already has `role="alert"` red text under the input, and a second announcement
            of the same verdict would just say it twice. */}
        <Icon style={{ color: verified ? '#16a34a' : '#dc2626' }} aria-label={label} />
      </span>
    </Tooltip>
  );
}

/** One fixed box, so the glyph's three states and its absence all occupy the same width — no
 *  nudging the text's right edge as a verdict arrives. Matches the 14px AntD icon. */
const EMAIL_GLYPH_BOX: React.CSSProperties = {
  display: 'inline-flex',
  width: 14,
  justifyContent: 'center',
};

function FieldControl({
  field,
  value,
  onChange,
  onBlur,
  language,
  invalid,
  mode,
  readOnly,
  addressAutocomplete,
  getFieldById,
  getValueById,
  setValueById,
  getBorrowedLayout,
  findFieldByRole,
  renderRevealSection,
  findUniquenessConflictMessage,
  a11y,
  dateRuleContext,
  getConditionValue,
  onRestrictionRefused,
}: {
  field: WorkflowField;
  value: FieldValue;
  onChange: (next: FieldValue) => void;
  /** Told when the field's input restriction refused something the advisor typed — the wrapper
   *  shows it, so "John*" says why the "*" did not appear (QA AH-004 / AH-009 / AH-010). */
  onRestrictionRefused?: (restriction: string) => void;
  /** Fires when an input loses focus — wired to the on-blur semantic validator. The Date
   *  control passes its raw typed text; see `handleBlur`. */
  onBlur?: (typedDate?: unknown) => void;
  language: 'en' | 'fr';
  /** True when the wrapper is showing an error for this field. Drives the red
   *  border on EVERY control below so the message + input read as one unit —
   *  a message under an untouched grey input gave the advisor nothing to scan
   *  for on a long failed step. */
  invalid?: boolean;
  /** Threaded through to the inline-reveal recursive FieldRenderer call so
   *  the borrowed field renders with the same mode. */
  mode?: 'preview' | 'advisor';
  /** See {@link Props.readOnly}. Merged into `disabled` below, and forwarded to the reveal
   *  slot's recursive render — a borrowed field must not stay live inside a locked form. */
  readOnly?: boolean;
  /** See {@link Props.addressAutocomplete}. */
  addressAutocomplete?: boolean;
  /** CheckboxGroup-only inline-reveal lookup — see Props on FieldRenderer. */
  getFieldById?: (id: string) => WorkflowField | undefined;
  getValueById?: (id: string) => FieldValue;
  setValueById?: (id: string, next: FieldValue) => void;
  getBorrowedLayout?: (id: string) => { columnCount: number; columnIndex: number } | undefined;
  /** Sibling lookup for the address-autocomplete branch (ShortText). */
  findFieldByRole?: (role: AddressRole) => WorkflowField | undefined;
  /** Renders a section borrowed by an option's `revealSectionId` inside that
   *  option's card — see Props on FieldRenderer. */
  renderRevealSection?: (sectionId: string, inheritedDisabled?: boolean) => ReactNode;
  /** Passed through the CheckboxGroup inline-reveal recursion so the
   *  revealed input still fires its cross-field uniqueness check. */
  findUniquenessConflictMessage?: (
    field: WorkflowField,
    value: FieldValue,
    ownValueKey?: string,
  ) => string | null;
  /** Ids + ARIA state assembled once by FieldRenderer — see FieldA11y. */
  a11y: FieldA11y;
  /** Date only — the outside inputs to the field's rules, so the picker greys exactly the
   *  days the blur check would refuse. */
  dateRuleContext?: DateRuleContext;
  /** Threaded through the inline-reveal recursion — see Props on FieldRenderer. */
  getConditionValue?: (id: string) => FieldValue;
}) {
  // Apply the field's input restriction and say so when it refused a character.
  const restrictTyped = (raw: string): void => {
    const kept = applyInputRestriction(field.inputRestriction, raw);
    onChange(kept);
    if (kept !== raw && field.inputRestriction) onRestrictionRefused?.(field.inputRestriction);
  };
  // The workflow's date format (QA D-01) — the Date picker's display, typed input and placeholder.
  const dateFormat = useContext(DateFormatContext);
  // AntD's `status` prop paints a red border that lines up with our own error
  // message below. Falls back to undefined so the default styling wins when
  // there's no error. Every single-control branch below MUST forward it —
  // Select / InputNumber / DatePicker each silently skipped it, so a dropdown
  // or an amount in error looked identical to an untouched one.
  const inputStatus = invalid ? ('error' as const) : undefined;
  // ZeroBounce's glyph for THIS field's current value — read from the same store the wrapper's
  // blur chain writes to, through the context rather than a prop (see emailVerifyContext on why
  // a dropped link here has to be impossible). Null for every field that isn't an email one,
  // and for an email one nobody has tabbed out of yet.
  const emailVerify = useEmailVerify();
  const emailIndicator =
    field.validate === 'email' && typeof value === 'string'
      ? emailValueIndicator(value, emailVerify?.lookup)
      : null;
  // WCAG wiring shared by every single-control branch. `id` is what the
  // field's <label for> targets; `aria-describedby` reaches the error / hint;
  // `aria-invalid` + `aria-required` expose state a red border and a red
  // asterisk convey only visually.
  const ariaProps = {
    id: a11y.controlId,
    'aria-describedby': a11y.describedBy,
    'aria-required': a11y.required || undefined,
    'aria-invalid': a11y.invalid || undefined,
    // Identify Input Purpose (1.3.5) — derived from addressRole / validate,
    // both already authored, so no form has to remember to set it.
    autoComplete: autocompleteFor(field),
  } as const;
  // The single disable point for every control this file mounts. Two independent reasons land
  // here: the FIELD is read-only (its value comes from prefill or a stored default, not from
  // advisor typing), or the whole SHELL is (BMO owns the application now, or `mode=r`).
  const disabled = field.readOnly === true || readOnly === true;
  const { t } = useTranslation();
  // Advisor profile — only consulted by the rep-code branch below. Module-cached
  // by useSession(), so this costs nothing per field.
  const { user: sessionUser } = useSession();
  // Per-option help opened from a card's info icon. Held here rather than in
  // OptionCardGroup so that component stays presentational.
  const [cardHelp, setCardHelp] = useState<{ title: string; body: string } | null>(null);
  const cardHelpModal = cardHelp ? (
    <Modal
      open
      className="bmo-help-modal"
      title={cardHelp.title}
      onCancel={() => setCardHelp(null)}
      footer={null}
      width={520}
      destroyOnClose
    >
      <HelpBodyMarkdown body={cardHelp.body} />
    </Modal>
  ) : null;
  // Per-option inline reveal (`option.revealFieldId`). Shared by the
  // RadioGroup and CheckboxGroup cases below — the two groups render their
  // options completely differently but the SLOT is identical, and letting the
  // two copies drift would mean a borrowed field behaving one way under a
  // radio and another under a checkbox.
  //
  // When the lookup props aren't passed (callers that don't know about
  // reveals) everything falls back to flat rendering.
  const canRenderReveal = Boolean(getFieldById) && Boolean(getValueById) && Boolean(setValueById);
  /** The field an option borrows, or null when it borrows nothing / the id is
   *  stale / this caller can't resolve reveals. */
  const revealTargetFor = (option: { revealFieldId?: string | null }): WorkflowField | null =>
    canRenderReveal && option.revealFieldId ? (getFieldById!(option.revealFieldId) ?? null) : null;
  /** The borrowed field, rendered in its authored column-grid context. */
  const renderRevealSlot = (reveal: WorkflowField) => {
    // Replay the authored row column-grid so a borrowed input the originator
    // placed in a 2-col row (with the sibling column empty) keeps its
    // half-width within the reveal slot. Empty siblings render as blank
    // placeholders to hold the grid's column tracks.
    const layout = getBorrowedLayout?.(reveal.id);
    const node = (
      <FieldRenderer
        field={reveal}
        value={getValueById!(reveal.id)}
        onChange={(next) => setValueById!(reveal.id, next)}
        language={language}
        mode={mode ?? 'preview'}
        // Without this a borrowed field under a checkbox stays live inside a locked form —
        // the reveal slot is the one write path a shell-level `disabled` doesn't reach.
        // `disabled`, not `readOnly`: a field disabled by its OWN read-only flag or by a
        // "Disable if" rule must take everything it reveals down with it, or the advisor
        // can still edit through the slot of a control they cannot touch.
        readOnly={disabled}
        addressAutocomplete={addressAutocomplete}
        getFieldById={getFieldById}
        getValueById={getValueById}
        setValueById={setValueById}
        getBorrowedLayout={getBorrowedLayout}
        findFieldByRole={findFieldByRole}
        // Thread cross-field uniqueness so a revealed ShortText tagged with
        // `uniquenessGroup` still fires its on-blur check inside the slot.
        findUniquenessConflictMessage={findUniquenessConflictMessage}
        getConditionValue={getConditionValue}
      />
    );
    if (!layout || layout.columnCount <= 1) return node;
    const cells = Array.from({ length: layout.columnCount }, (_, i) =>
      i === layout.columnIndex ? (
        <div key={i}>{node}</div>
      ) : (
        <div key={i} className="bmo-form-row__empty" />
      ),
    );
    return (
      // Same class as the real row this slot is imitating, so the borrowed field
      // collapses with it: a quarter-width input inside a reveal slot would
      // otherwise stay a quarter of a phone screen wide.
      <div
        className="bmo-form-row"
        data-cols={layout.columnCount}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${layout.columnCount}, minmax(0, 1fr))`,
          gap: 16,
        }}
      >
        {cells}
      </div>
    );
  };
  /**
   * Everything an option reveals, as one node — a borrowed SECTION when it
   * names one, otherwise its borrowed FIELD, otherwise null.
   *
   * Section reveal is gated only on `renderRevealSection`: unlike a field
   * reveal it needs no id/value/setter lookups, because FormRenderer's closure
   * already carries the whole section's render context.
   *
   * The domain refuses both properties on one option, so the branch order only
   * decides a winner for stale hand-authored data — section first, since it is
   * the larger and more visible of the two.
   */
  const revealNodeFor = (option: {
    revealFieldId?: string | null;
    revealSectionId?: string | null;
  }): ReactNode => {
    if (option.revealSectionId && renderRevealSection) {
      // Same inheritance as renderRevealSlot above — a borrowed SECTION under a disabled
      // control is disabled too.
      return renderRevealSection(option.revealSectionId, disabled);
    }
    const target = revealTargetFor(option);
    return target ? renderRevealSlot(target) : null;
  };
  /** Does this group borrow anything at all? Drives the "keep the original flat
   *  DOM when nothing is revealed" shortcut on both group renderers. */
  const groupHasReveals = field.options?.some((o) => o.revealFieldId || o.revealSectionId);
  switch (field.type) {
    case 'ShortText': {
      // When the field carries a masked validate rule (sin / phone / postal),
      // the mask owns the character set + position layout AND the length
      // cap — applying inputRestriction or DOM maxLength on top would either
      // strip the mask's separator characters or clip the formatted output
      // before the advisor finishes typing. Email + non-validate-set fields
      // keep their originator-set maxLength.
      const masked = isMaskedRule(field.validate);
      // AC3 — an advisor carrying SEVERAL representative codes picks which one
      // this application is filed under. The field stays originator-authored as
      // a read-only prefill; we only swap the control when there is an actual
      // choice to make. One code (or none — originator preview, or a failed
      // GAMX lookup) falls through to the plain read-only Input, which
      // FormShell has already prefilled.
      if (field.sourceValue === 'advisor.repCode' && needsRepCodeChoice(sessionUser)) {
        return (
          <Select
            {...ariaProps}
            value={(value as string | null | undefined) || undefined}
            placeholder={t('workflow.builder.properties.sourceValueRepCodeSelect')}
            status={inputStatus}
            style={{ width: '100%' }}
            // This branch used to ignore `disabled` entirely, so a locked application left the
            // rep-code picker live. FormShell's dead onChange hid that for the shell lock; a
            // rule-disabled field has no such fallback, since the rest of the step stays live.
            disabled={disabled}
            onBlur={onBlur}
            onChange={(next: string) => onChange(next)}
            options={(sessionUser?.representativeCodes ?? []).map((code) => ({
              value: code,
              label: code,
            }))}
          />
        );
      }
      // Display mask (QA D-21) — shown formatted, STORED as digits (see displayMask.ts for why
      // this is the opposite of the sin / phone / postal rules above). Never both: the domain
      // refuses a display mask beside a format-storing rule.
      const displayMask = !masked && field.displayMask ? field.displayMask : null;
      const handleChange = (raw: string) => {
        if (masked) {
          onChange(applyFormatMask(field.validate, raw));
        } else if (displayMask) {
          onChange(maskedDigits(displayMask, raw));
        } else {
          restrictTyped(raw);
        }
      };
      // Canada Post AddressComplete branch. Lights up when the originator
      // flipped the autocomplete property on the field. We need the
      // sibling-population callbacks (setValueById + findFieldByRole) —
      // if a callsite didn't pass them, fall back to the plain Input.
      // Two independent yeses are required: the runtime capability
      // (addressAutocomplete, false only for the public customer runtime) and the
      // originator's per-field authoring choice. Deliberately NOT implemented by
      // withholding setValueById - that prop also drives radio/checkbox reveal slots and
      // repeatable dual-unit columns, both of which a public customer needs.
      if (
        addressAutocomplete !== false &&
        field.addressAutocomplete === 'canada-post' &&
        setValueById &&
        findFieldByRole
      ) {
        return (
          <AddressAutocompleteInput
            ariaProps={ariaProps}
            field={field}
            value={(value as string | null | undefined) ?? ''}
            inputStatus={inputStatus}
            onChange={handleChange}
            onBlur={onBlur}
            setValueById={setValueById}
            findFieldByRole={findFieldByRole}
            // See the rep-code Select above — this branch had the same gap, and its
            // sibling-populating write path is one a `disabled` attribute alone never reached.
            disabled={disabled}
          />
        );
      }
      return (
        <Input
          {...ariaProps}
          value={
            displayMask
              ? formatWithMask(displayMask, maskedDigits(displayMask, String(value ?? '')))
              : ((value as string | null | undefined) ?? '')
          }
          placeholder={field.placeholder ?? displayMask ?? undefined}
          // The mask owns the length when there is one: the formatted text is exactly as long as
          // the mask, so a digit-count maxLength would clip it before the value is complete.
          // Unmasked and uncapped falls back to DEFAULT_MAX_LENGTH rather than staying unbounded.
          maxLength={
            displayMask
              ? displayMask.length
              : masked
                ? undefined
                : effectiveMaxLength('ShortText', field.maxLength, field.minLength)
          }
          inputMode={displayMask ? 'numeric' : undefined}
          status={inputStatus}
          // The ZeroBounce glyph, for an email field only. Passed for EVERY email field even
          // when there is nothing to draw: AntD only wraps the <input> in a suffix container
          // when this prop is present, so toggling it on the first verdict would restructure
          // the DOM under a field the advisor may have clicked back into and drop their focus
          // mid-keystroke. A fixed-width placeholder keeps the shape — and the text's right
          // edge — still across the whole spinner → verdict sequence.
          suffix={
            field.validate === 'email' ? (
              <EmailVerifyFieldIcon indicator={emailIndicator} field={field} language={language} />
            ) : undefined
          }
          disabled={disabled}
          onBlur={onBlur}
          onChange={(e) => handleChange(e.target.value)}
        />
      );
    }
    case 'LongText':
      return (
        <Input.TextArea
          {...ariaProps}
          value={(value as string | null | undefined) ?? ''}
          placeholder={field.placeholder ?? undefined}
          maxLength={effectiveMaxLength('LongText', field.maxLength, field.minLength)}
          // "80/999" under the bottom-right corner, per the 2026-07-24 BMO
          // design. Stays tied to the AUTHORED cap: every LongText now carries
          // an effective one, and showing the counter on all of them would add
          // a visible element to every published form nobody asked for.
          showCount={field.maxLength ? true : undefined}
          rows={4}
          status={inputStatus}
          disabled={disabled}
          onBlur={onBlur}
          onChange={(e) => restrictTyped(e.target.value)}
        />
      );
    case 'Number': {
      // numberFormat lights up a "$" prefix (currency) or "%" suffix
      // (percentage). The formatter inserts thousand-separator commas while
      // typing; the parser strips everything that isn't a digit, dot, or
      // minus so the value reaching state is always a plain number — letters
      // pasted from somewhere else won't survive.
      const fmt = field.numberFormat;
      const isCurrency = fmt === 'currency';
      const isPercentage = fmt === 'percentage';
      const stripNonNumeric = (s: string | undefined) => (s ?? '').replace(/[^0-9.-]/g, '');
      // Shared with the repeatable money column so the two controls cannot drift — see
      // thousandsFormatter, which also keeps the grouping off the decimal tail.
      const formatter = isCurrency ? thousandsFormatter : undefined;
      // Money is capped at cents on EVERY route in, not just keystrokes (QA D-31).
      const parser = isCurrency
        ? numericParser({
            allowNegative: (field.min ?? Number.NEGATIVE_INFINITY) < 0,
            maxDecimals: 2,
          })
        : isPercentage
          ? stripNonNumeric
          : undefined;
      return (
        <InputNumber
          {...ariaProps}
          value={(value as number | null | undefined) ?? null}
          // No `min` / `max` here on purpose. AntD treats them as a bound to SNAP to: it parks
          // the out-of-range figure on screen while withholding its `onChange` (so a max of 999
          // showed `1000` with 100 still in state), then rewrites it to the bound on blur —
          // a silent change to the advisor's money. The bounds are a named rule instead
          // (`fieldNumberRangeViolation` below, and FormShell's Next gate), exactly as they
          // already are in a repeatable Number column. What the control does enforce is the
          // field's WIDTH — see the digit cap on the guard below.
          step={field.step ?? undefined}
          precision={field.integerOnly ? 0 : undefined}
          status={inputStatus}
          style={{ width: '100%' }}
          prefix={isCurrency ? '$' : undefined}
          suffix={isPercentage ? '%' : undefined}
          formatter={formatter}
          parser={parser}
          controls={false}
          disabled={disabled}
          // Every other control has always forwarded this; the Number one never did, so no
          // blur rule had ever run on it — not the range rule below, and not the
          // required-empty check its siblings have always shown.
          onBlur={onBlur}
          // The parser alone does NOT stop letters being typed — AntD keeps the
          // raw text whenever it cannot derive a number, so `ABCD` stays on
          // screen. This refuses the character at entry.
          {...numericInputGuard({
            allowDecimal: !field.integerOnly,
            allowNegative: (field.min ?? Number.NEGATIVE_INFINITY) < 0,
            // Money has cents and nothing finer — a third decimal is refused as it is typed
            // (QA D-41), rather than kept on screen and silently rounded later.
            maxDecimals: isCurrency ? 2 : undefined,
            // The authored Maximum sizes the box: 999 takes any three digits, and the fourth
            // is refused at the keystroke. A figure that fits but is still over the maximum
            // (800 under a max of 123) is kept and NAMED on blur — the width and the value are
            // two different rules. See integerDigitCap.
            maxIntegerDigits: integerDigitCap(field.min, field.max),
          })}
          onChange={(v) => onChange(v as FieldValue)}
        />
      );
    }
    case 'SingleSelect':
      // Advisor representative-code picker — the dropdown's choices come from
      // the signed-in advisor's session (with a sample fallback in preview),
      // not from `field.options` (the domain refuses to author any on such a
      // field). No `allowClear`: the slot is always Required, and clearing a
      // rep code the advisor is legally filing under is not a meaningful
      // action the way clearing an ordinary dropdown answer is.
      if (field.advisorRepCodeSelector) {
        return (
          <Select
            {...ariaProps}
            value={(value as string | null | undefined) || undefined}
            placeholder={t('workflow.builder.properties.sourceValueRepCodeSelect')}
            status={inputStatus}
            style={{ width: '100%' }}
            onBlur={onBlur}
            onChange={(v: string) => onChange(v)}
            disabled={disabled}
            options={repCodeOptionsFor(sessionUser, mode ?? 'advisor').map((code) => ({
              value: code,
              label: code,
            }))}
          />
        );
      }
      return (
        <Select
          {...ariaProps}
          value={(value as string | null | undefined) ?? undefined}
          // BMO's stories give every dropdown the hint "Select" (QA D8); an authored placeholder wins.
          placeholder={field.placeholder ?? t('formRenderer.selectPlaceholder')}
          status={inputStatus}
          style={{ width: '100%' }}
          options={field.options.map((o) => ({
            value: o.value,
            label: language === 'fr' && o.labelFr ? o.labelFr : o.labelEn,
          }))}
          onChange={(v) => onChange(v ?? null)}
          allowClear
          disabled={disabled}
        />
      );
    case 'RadioGroup':
      // `displayAs: 'card'` is CHROME and `verticalOptions` is DIRECTION —
      // they combine rather than compete:
      //   card + horizontal → tile grid (icon above a centred label)
      //   card + vertical   → full-width bordered rows, control on the left
      // Same value semantics in every case; only presentation differs, so
      // conditions, validation, blocking values and PDF mappings are
      // untouched. Icons are a tile-grid affordance and aren't drawn on rows.
      //
      // Reveals are unsupported on the TILE grid only, exactly as for
      // CheckboxGroup: a borrowed input has nowhere sensible to live inside a
      // fixed-width centred tile and squeezing one in breaks the grid
      // alignment. Both list paths (plain rows and vertical cards) support
      // them via BmoRadioGroup's `renderAfter` slot.
      if (field.displayAs === 'card' && !field.verticalOptions) {
        return (
          <>
            <OptionCardGroup
              ariaInvalid={a11y.invalid}
              value={(value as string | undefined) ?? undefined}
              onChange={(next) => onChange(next as string)}
              options={field.options.map((o) => ({
                value: o.value,
                label: language === 'fr' && o.labelFr ? o.labelFr : o.labelEn,
                icon: o.icon,
                helpTitle: language === 'fr' && o.helpTitleFr ? o.helpTitleFr : o.helpTitleEn,
                helpBody: language === 'fr' && o.helpBodyFr ? o.helpBodyFr : o.helpBodyEn,
              }))}
              disabled={disabled}
              onOptionHelp={setCardHelp}
            />
            {cardHelpModal}
          </>
        );
      }
      return (
        // BmoRadioGroup renders the BMO design spec (24px blue ring + 12px
        // dot, 2px border) — the AntD Radio.Group default ring/dot are the
        // wrong size + weight for the BMO surface.
        <>
          <BmoRadioGroup<string>
            ariaLabelledBy={a11y.labelId}
            ariaDescribedBy={a11y.describedBy}
            ariaRequired={a11y.required}
            ariaInvalid={a11y.invalid}
            value={(value as string | undefined) ?? undefined}
            onChange={(next) => onChange(next)}
            // An optional question can be un-answered by clicking its choice again (QA TFSA-D02).
            onDeselect={field.required ? undefined : () => onChange(null)}
            options={field.options.map((o) => ({
              value: o.value,
              label: language === 'fr' && o.labelFr ? o.labelFr : o.labelEn,
              // An option's own help (QA RIF TC-09-07: "Maximum annual payment") — the card tiles
              // already offered it; a plain list never drew it.
              ...(o.helpBodyEn
                ? {
                    helpLabel: t('formRenderer.openHelp'),
                    onHelp: () =>
                      setCardHelp({
                        title:
                          (language === 'fr' && o.helpTitleFr ? o.helpTitleFr : o.helpTitleEn) ??
                          '',
                        body:
                          (language === 'fr' && o.helpBodyFr ? o.helpBodyFr : o.helpBodyEn) ?? '',
                      }),
                  }
                : {}),
            }))}
            disabled={disabled}
            vertical={!!field.verticalOptions}
            cards={field.displayAs === 'card'}
            // Only handed over when this group actually borrows something, so a
            // radio with no reveals keeps BmoRadioGroup's original flat DOM.
            renderAfter={
              groupHasReveals
                ? (optionValue) => {
                    const option = field.options.find((o) => o.value === optionValue);
                    return option ? revealNodeFor(option) : null;
                  }
                : undefined
            }
          />
          {cardHelpModal}
        </>
      );
    case 'CheckboxGroup': {
      const checkedValues = (value as string[] | undefined) ?? [];
      // Mutual exclusion between options DISABLES, it never clears (QA INV-004): ticking one greys
      // out the options it conflicts with rather than silently unticking them. The advisor unticks
      // the other side themselves instead of watching their answers — and any inline reveal hanging
      // off them — vanish without a signal.
      //
      // `clearsOnSelect` keeps its stored name (renaming it would mean migrating every seed snapshot
      // and the persisted DDB rows for no behavioural gain) but now reads as a declaration of
      // CONFLICT. Two options conflict when EITHER names the other. Reading the declaration both
      // ways is load-bearing, not defensive: the TFSA group declares the rule only on "I am not
      // contributing" (see catalogFields.ts), so a forward-only reading would leave that option
      // tickable on top of a ticked sibling — the exact stranded-answer state this prevents.
      const conflicts = (
        a: (typeof field.options)[number],
        b: (typeof field.options)[number],
      ): boolean =>
        (a.clearsOnSelect ?? []).includes(b.value) || (b.clearsOnSelect ?? []).includes(a.value);
      // Not ticked itself, and in conflict with something that is. An option that is ALREADY ticked
      // is never disabled, so a stale answer holding both sides of a pair can always be undone.
      const isBlocked = (o: (typeof field.options)[number]): boolean =>
        !checkedValues.includes(o.value) &&
        field.options.some(
          (x) => x.value !== o.value && checkedValues.includes(x.value) && conflicts(o, x),
        );
      // Per-option reveal: when an option carries a `revealFieldId` that
      // resolves to another field on the step, the borrowed field is rendered
      // immediately UNDER the checkbox (indented to line up with the label),
      // still scoped to the option's row. Stacking under (vs to the right
      // of) the checkbox keeps the option labels vertically consistent no
      // matter how tall any one reveal turns out to be — the inline-grid
      // version pushed the next checkbox down by the reveal's full height
      // and read as a broken layout. Visibility is owned by the parent
      // option's checked state, not by the borrowed field's own conditions
      // (which are bypassed entirely for borrowed fields by the section
      // walker). Resolution + the slot itself come from `revealTargetFor` /
      // `renderRevealSlot` above, shared with the RadioGroup case.
      // Toggle a single option's checked state. The answer is written straight through — mutual
      // exclusion is enforced up front by `isBlocked` disabling the conflicting boxes, so there is
      // nothing to prune on the way out. Keeps the design (24px BMO-blue square) parity with
      // BmoRadioGroup.
      const toggleOption = (val: string, on: boolean): void => {
        const nextSet = new Set(checkedValues);
        if (on) nextSet.add(val);
        else nextSet.delete(val);
        onChange(Array.from(nextSet));
      };
      // Originator-picked layout (WorkflowField.verticalOptions): vertical
      // stacks one option per line; the default flows options horizontally
      // with wrap (mirrors .bmo-radio-group so radio + checkbox groups read
      // the same). Each option keeps its own inner column so a reveal slot
      // still renders beneath its checkbox in either layout.
      const verticalLayout = !!field.verticalOptions;
      // Full-width bordered row per option — card chrome over the list
      // renderer, so reveals and mutual exclusion are unaffected.
      const rowCards = field.displayAs === 'card' && verticalLayout;
      // Card layout — `isBlocked` is handed to the same per-option `disabled` prop the list layout
      // uses, so per-option `clearsOnSelect` mutual-exclusion behaves identically in both.
      //
      // Reveals are unsupported on the TILE grid only: a borrowed input has
      // nowhere sensible to live inside a fixed-width centred tile, and
      // squeezing one in breaks the grid alignment. The vertical card path
      // below has no such problem — it is the ordinary list renderer with a
      // border around each option, so reveals and `clearsOnSelect` work
      // exactly as they do in a plain list. That is the shape the BMO
      // "Mode of Contribution" design asks for.
      if (field.displayAs === 'card' && !verticalLayout) {
        return (
          <>
            <OptionCardGroup
              multiple
              ariaInvalid={a11y.invalid}
              value={checkedValues}
              onChange={(next) => onChange(next as string[])}
              options={field.options.map((o) => ({
                value: o.value,
                label: language === 'fr' && o.labelFr ? o.labelFr : o.labelEn,
                icon: o.icon,
                helpTitle: language === 'fr' && o.helpTitleFr ? o.helpTitleFr : o.helpTitleEn,
                helpBody: language === 'fr' && o.helpBodyFr ? o.helpBodyFr : o.helpBodyEn,
                disabled: isBlocked(o),
              }))}
              disabled={disabled}
              onOptionHelp={setCardHelp}
            />
            {cardHelpModal}
          </>
        );
      }
      return (
        // Flex column per option — checkbox-with-label on top, reveal slot
        // beneath when checked. `bmo-checkbox--top` keeps the box aligned
        // with the first line of a wrapping label (matches paper-form
        // reading order). The 26-px reveal indent aligns the borrowed
        // input with the option label rather than the tick box itself.
        <div
          style={
            verticalLayout
              ? { display: 'flex', flexDirection: 'column', width: '100%' }
              : {
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  columnGap: 24,
                  width: '100%',
                }
          }
        >
          {field.options.map((o) => {
            const checked = checkedValues.includes(o.value);
            const reveal = revealNodeFor(o);
            const showRevealSlot = checked && reveal !== null;
            return (
              <div
                key={o.value}
                className={rowCards ? 'bmo-option-row-card' : undefined}
                style={{ display: 'flex', flexDirection: 'column', marginBottom: 10 }}
              >
                <BmoCheckbox
                  checked={checked}
                  onChange={(next) => toggleOption(o.value, next)}
                  disabled={disabled || isBlocked(o)}
                  className="bmo-checkbox--top"
                  // The group's error belongs to the QUESTION, so every option
                  // carries it — the red box is what the advisor scans for, and
                  // marking only one would point at an arbitrary option.
                  ariaInvalid={a11y.invalid}
                >
                  {language === 'fr' && o.labelFr ? o.labelFr : o.labelEn}
                </BmoCheckbox>
                {showRevealSlot && reveal && (
                  // Inside a card the reveal lines up with the CONTROL's left
                  // edge (the card padding already provides the inset); in a
                  // plain list it indents past the tick box to sit under the
                  // option label.
                  <div style={{ marginLeft: rowCards ? 0 : 26, marginTop: 12 }}>{reveal}</div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    case 'Date': {
      // Stored as ISO yyyy-MM-dd (string) on the values map so it serializes
      // cleanly. dayjs handles the round-trip with the picker's Dayjs API.
      const raw = (value as string | null | undefined) ?? null;
      const parsed: Dayjs | null = raw ? dayjs(raw) : null;
      const safe = parsed && parsed.isValid() ? parsed : null;
      // The workflow's format (`dateFormat` above) — DD/MM/YYYY unless the workflow chose
      // MM/DD/YYYY. AntD's DatePicker accepts typed input that parses against it, so under the
      // default `13/10/1990` is valid. Storage stays ISO whatever the display.
      // Grey every day the field's own rules would refuse (min-age / no-future,
      // no-past, must-follow) — the same `validateDateRules` the blur check and
      // the Next gate call, so the calendar cannot offer a day they reject.
      const hasDateRule =
        field.minAge != null ||
        field.maxAge != null ||
        !!field.noPastDates ||
        !!field.mustFollowFieldId;
      const disabledDate = hasDateRule
        ? (d: Dayjs) => validateDateRules(field, d.format('YYYY-MM-DD'), dateRuleContext) !== null
        : undefined;
      // Greying is not enough for the calendar's year and decade views: BMO asked that a date
      // of birth not OFFER future years at all (INFLOWS-486 #5). The picker's own bounds stop
      // it paging past them — the latest allowed day for min-age / no-future, today for no-past.
      const maxDate =
        field.minAge != null ? dayjs().subtract(field.minAge, 'year').endOf('day') : undefined;
      const minDate = field.noPastDates
        ? dayjs().startOf('day')
        : field.maxAge != null
          ? dayjs().subtract(field.maxAge, 'year').startOf('day')
          : undefined;
      // The bounds grey the rest of the current decade; blank those year / decade cells so an
      // out-of-range year is not shown at all.
      const outOfRangeYear = (d: Dayjs, span: number) =>
        (maxDate != null && d.year() > maxDate.year()) ||
        (minDate != null && d.year() + span - 1 < minDate.year());
      const cellRender: DatePickerProps['cellRender'] = (current, info) => {
        if (!dayjs.isDayjs(current) || !(maxDate || minDate)) return info.originNode;
        if (info.type === 'year' && outOfRangeYear(current, 1)) return null;
        if (info.type === 'decade' && outOfRangeYear(current, 10)) return null;
        return info.originNode;
      };
      return (
        // onKeyUp must live on this wrapper, not on <BmoDatePicker> itself — antd
        // doesn't forward an unrecognised onKeyUp prop to the underlying
        // <input>, but it still bubbles up through the DOM to here. The div
        // itself isn't interactive — the picker's own <input> already owns
        // focus/tab/keyboard — this is purely a bubble-listener relay.
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div onKeyUp={clearOnEmptyBackspace(() => onChange(null))}>
          <BmoDatePicker
            {...ariaProps}
            status={inputStatus}
            style={{ width: '100%' }}
            value={safe}
            format={dateFormat}
            // Placeholder shows the same format so typing intent is obvious — in the reader's language.
            placeholder={formatPlaceholder(dateFormat, language)}
            disabledDate={disabledDate}
            maxDate={maxDate}
            minDate={minDate}
            cellRender={cellRender}
            disabled={disabled}
            onChange={(next) => onChange(next ? next.format('YYYY-MM-DD') : null)}
            // Hand the raw text to the validator: by the time the value settles, AntD has
            // already dropped a typed date it couldn't accept.
            onBlur={(e) => onBlur?.((e.target as HTMLInputElement).value)}
            // BMO-blue calendar suffix — AntD's default is gray; the BMO design
            // ref uses the brand blue here.
            suffixIcon={<CalendarOutlined style={{ color: '#0179C1' }} />}
          />
        </div>
      );
    }
    case 'Toggle': {
      // The Switch renders no text of its own, so the label has to be painted
      // beside it — exactly as the Checkbox case below paints its own. Until
      // 2026-10-21 this branch returned the bare Switch, which left the
      // originator-authored question reachable by screen reader (`aria-label`)
      // and invisible to everyone else.
      //
      // `hideLabel` is the opt-OUT, so the default is shown: the flag means the
      // same thing here as everywhere else — a preceding StaticText already
      // asks the question and a repeat would be noise.
      const labelHidden = field.hideLabel === true;
      const toggle = (
        <Switch
          id={a11y.controlId}
          checked={!!value}
          onChange={(checked) => onChange(checked)}
          disabled={disabled}
          // Named by the VISIBLE copy whenever there is any: an `aria-label`
          // alongside it would win over the text on screen and is then free to
          // drift from it. Only a hidden label falls back to the string.
          aria-label={labelHidden ? a11y.labelText : undefined}
          aria-labelledby={labelHidden ? undefined : a11y.labelId}
          aria-describedby={a11y.describedBy}
          aria-required={a11y.required || undefined}
        />
      );
      if (labelHidden) return toggle;
      return (
        <div className="bmo-toggle-field">
          {toggle}
          {/* Clicking the label flips the switch — what the wrapping <label>
              hands the Checkbox for free, and what an AntD Switch cannot have:
              it renders a <button role="switch">, which `htmlFor` does not
              drive. The switch itself stays the focusable, keyboard-operable
              control; this span only adds a pointer target on top of it, so
              there is no keyboard handler for the jsx-a11y pair to ask for. */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <span
            id={a11y.labelId}
            className="bmo-toggle-field__label"
            onClick={(e) => {
              // A markdown link in the copy — "I agree to the [terms](…)" — is
              // its own destination. Following it must not also answer the
              // question it sits inside.
              if (disabled || (e.target as HTMLElement).closest('a')) return;
              onChange(!value);
            }}
          >
            <RichText body={labelFor(field, language)} variant="inline" />
            {field.required && <RequiredMark />}
          </span>
        </div>
      );
    }
    case 'Checkbox':
      return (
        <BmoCheckbox
          id={a11y.controlId}
          checked={!!value}
          onChange={(next) => onChange(next)}
          disabled={disabled}
          // Marks the one case where the inline label IS the question rather
          // than an answer — the error CSS reddens that text, but not a
          // CheckboxGroup option's.
          className="bmo-checkbox--standalone"
          ariaDescribedBy={a11y.describedBy}
          ariaRequired={a11y.required}
          ariaInvalid={a11y.invalid}
        >
          {/* hideLabel applies to the inline label too — the `skipLabel`
              short-circuit at the wrapper covered the above-control
              FieldLabel but Checkbox / Toggle ALSO render their own
              inline label below, which used to ignore the flag. */}
          {field.hideLabel ? null : (
            <>
              <RichText body={labelFor(field, language)} variant="inline" />
              {field.required && <RequiredMark />}
            </>
          )}
        </BmoCheckbox>
      );
    case 'StaticText':
    case 'Paragraph':
    case 'Divider':
    case 'BulletedList':
    case 'Notice':
    case 'Image':
      // Handled above the switch — layout blocks short-circuit before
      // FieldControl is mounted. Returning null keeps the type-checker happy
      // for exhaustiveness.
      return null;
    case 'RepeatableRow':
      return (
        <RepeatableRowControl
          field={field}
          value={Array.isArray(value) ? (value as RepeatableRowValue[]) : []}
          onChange={onChange as (next: RepeatableRowValue[]) => void}
          language={language}
          getValueById={getValueById}
          setValueById={setValueById}
          disabled={disabled}
        />
      );
  }
}

/**
 * Canada Post AddressComplete autocomplete on a ShortText field. The
 * input behaves like the plain ShortText except that as the advisor
 * types, we debounce a `Find` request to the BE proxy, render the
 * results as an AntD AutoComplete dropdown, and on selection either
 * drill into a `Next=Find` suggestion or fire a `Retrieve` against a
 * leaf suggestion.
 *
 * On `Retrieve` success we populate the current field with the street
 * line AND cross-fill sibling fields on the same step by label match
 * (City, Province, Postal Code, Apartment). Province dropdown values
 * match against the 2-letter code; everything else is plain text.
 *
 * Wired in by the parent FieldControl when
 * `field.addressAutocomplete === 'canada-post'` and the lookup
 * callbacks (`setValueById`, `findFieldByRole`) are available.
 */
function AddressAutocompleteInput({
  field,
  value,
  inputStatus,
  onChange,
  onBlur,
  setValueById,
  findFieldByRole,
  ariaProps,
  disabled,
}: {
  field: WorkflowField;
  value: string;
  inputStatus: 'error' | undefined;
  onChange: (raw: string) => void;
  onBlur?: () => void;
  setValueById: (id: string, next: FieldValue) => void;
  findFieldByRole: (role: AddressRole) => WorkflowField | undefined;
  /** Merged from the field's own read-only flag, its "Disable if" rule, and the shell
   *  lock — see FieldControl's single disable point. This branch is one of three that
   *  used to ignore it, which left a locked form's address lookup fully live. */
  disabled?: boolean;
  /** id / aria-describedby / aria-invalid / aria-required from the field
   *  contract — spread onto the AutoComplete so this branch is labelled and
   *  described exactly like the plain Input it replaces. */
  ariaProps: Record<string, string | boolean | undefined>;
}) {
  const [options, setOptions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  // Controlled dropdown-visibility state so we can force it open after a
  // drill selection. AntD closes the dropdown on every `onSelect`, so a
  // drill row (Next=Find) that fires a follow-up runFind gets its 9-leaf
  // response into `options` while the dropdown is invisible — the user
  // never sees the leaves. Owning `open` lets us re-show the dropdown
  // once new options arrive.
  const [open, setOpen] = useState(false);
  // Tracks whether the last user gesture on this input was a DRILL select
  // (Next=Find). When true, we know to reopen the dropdown as soon as the
  // follow-up runFind returns — but only for that one gesture, so a
  // plain leaf select still closes the dropdown normally.
  const drillingRef = useRef(false);
  // The exact text AddressComplete itself wrote into the input on the last
  // select, held until that select RESOLVES into a structured address.
  //
  // Selecting a condo / multi-unit row (Next=Find) makes AntD write the row's
  // display value — "46 GLAMIS GREEN SW, CALGARY — 12 Addresses" — into the
  // field and fire onChange, so the drill hint lands in form state. Abandon the
  // drill (click away, tab out) and that string stays: it reads as a completed
  // address, prints onto PDFs, and satisfies a required check, while city /
  // province / postal are all empty because no Retrieve ever ran.
  //
  // Null means "whatever is in this box belongs to the person typing" — which
  // is the state for a hand-typed address, and the state we return to the
  // instant they edit over our text (see handleValueChange). Only a value this
  // component wrote AND that is still verbatim what it wrote is ever cleared.
  const unresolvedLookupRef = useRef<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic sequence — every Find call captures its seq; setOptions
  // is gated on `seq === latestRef.current` so a slow earlier response
  // can't overwrite a faster newer one when the user types quickly.
  const latestFindSeqRef = useRef(0);
  // Cancel any pending debounce on unmount so a late callback doesn't
  // try to setState after the field disappears. Bumping the seq also
  // discards any already-in-flight responses.
  useEffect(
    () => () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      latestFindSeqRef.current += 1;
    },
    [],
  );

  const runFind = (query: string, drillLastId: string | null) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (!query.trim()) {
      setOptions([]);
      return;
    }
    debounceTimerRef.current = setTimeout(async () => {
      const seq = ++latestFindSeqRef.current;
      setLoading(true);
      try {
        const results = await workflowsApi.addressLookupFind(query, 'CAN', drillLastId);
        if (seq !== latestFindSeqRef.current) return;
        setOptions(results);
        // One-shot: the onDropdownVisibleChange guard kept the dropdown
        // open through the drill's onSelect close, and the new leaves are
        // now in options[]. Clear drillingRef so a subsequent plain-leaf
        // select closes the dropdown normally like before.
        if (drillingRef.current) drillingRef.current = false;
      } catch {
        if (seq !== latestFindSeqRef.current) return;
        // Provider hiccup — show no suggestions; advisor types manually.
        // Also clear drillingRef so the onDropdownVisibleChange guard
        // doesn't strand the dropdown open with 0 options — otherwise
        // blur / Escape / outside-click all get swallowed until the user
        // types (which handleSearch clears the flag).
        setOptions([]);
        drillingRef.current = false;
      } finally {
        if (seq === latestFindSeqRef.current) setLoading(false);
      }
    }, 250);
  };

  const handleSearch = (q: string) => {
    // User typed → not drilling. Any in-flight drill from an older gesture
    // should be cancelled by the sequence counter anyway, but flip the
    // flag defensively.
    drillingRef.current = false;
    runFind(q, null);
  };

  /**
   * Every write to the input funnels through here — keystrokes AND the value
   * AntD echoes back on a select.
   *
   * Any text that differs from what we last wrote is the person's own, so the
   * component releases its claim on it. That is the whole safety interlock for
   * the blur-clear below: type over an abandoned drill hint, or type an address
   * by hand and never touch the dropdown, and nothing is ever auto-removed.
   */
  const handleValueChange = (next: string) => {
    if (unresolvedLookupRef.current !== null && next !== unresolvedLookupRef.current) {
      unresolvedLookupRef.current = null;
    }
    onChange(next);
  };

  /**
   * Leaving the field with a lookup still half-finished drops the text
   * AddressComplete put there — an unfinished drill ("… — 12 Addresses"), or a
   * leaf whose Retrieve never landed. An empty box is honest about the state:
   * nothing was chosen, so a required field paints red and the advisor is told,
   * rather than carrying a half-address through to a PDF with no city or
   * postal code beside it.
   *
   * Gated on the value still being verbatim what we wrote, so this can never
   * reach a hand-typed address (handleValueChange has already nulled the ref by
   * then). Clearing before `onBlur` matters: the parent's validation reads the
   * value committed in this same tick, so the required message appears now
   * instead of one blur late.
   */
  const handleBlur = () => {
    if (unresolvedLookupRef.current !== null && value === unresolvedLookupRef.current) {
      unresolvedLookupRef.current = null;
      drillingRef.current = false;
      setOptions([]);
      setOpen(false);
      onChange('');
    }
    onBlur?.();
  };

  const handleSelect = async (text: string, option: { id?: string; next?: string }) => {
    // AntD v5's AutoComplete sometimes strips custom fields (`id`, `next`)
    // off the option before firing `onSelect` — the callback then arrives
    // with only `value` + `label`, and the retrieve never fires. Look the
    // selected row back up by matching its display value against the
    // current options[] list as a defence-in-depth fallback.
    const match = options.find(
      (o) => (o.description ? `${o.text} ${o.description}` : o.text) === text,
    );
    const id = option.id ?? match?.id ?? '';
    const next = option.next ?? match?.next ?? 'Retrieve';
    // AntD has already written this row's display text into the input (and
    // fired onChange with it). Claim it as ours-and-unresolved until a Retrieve
    // replaces it with a real address; abandoning the lookup now empties the
    // box instead of leaving a drill hint behind. Set before the `!id` bail so
    // an option that arrives without one — the stripped-fields case the lookup
    // above defends against — can't strand its text either.
    // Claiming it on EVERY select, not just a drill, is deliberate: the ref is
    // released the moment the person edits the text, so the only values this
    // can ever clear are ones no Retrieve resolved.
    unresolvedLookupRef.current = text;
    if (!id) return;
    if (next === 'Find') {
      // Drill — re-run Find scoped to this row. Flag so the follow-up
      // Find response knows to reopen the dropdown (AntD closed it on
      // this select). Use the ORIGINAL row's `text` as the search seed —
      // the value stored on the input already includes the "- N Addresses"
      // drill hint from the description, which Canada Post's Find scores
      // poorly.
      drillingRef.current = true;
      const searchSeed = match?.text ?? text;
      runFind(searchSeed, id);
      return;
    }
    // Retrieve path — pull the structured address and populate siblings.
    // Bump the seq so any in-flight Find response from the same select
    // gesture is discarded (its results would be stale next to Retrieve).
    latestFindSeqRef.current += 1;
    setLoading(true);
    try {
      const details = await workflowsApi.addressLookupRetrieve(id);
      if (!details) return;
      // Resolved — the writes below are a real address, so release the claim
      // and let it stand on its own like any typed value.
      unresolvedLookupRef.current = null;
      const fullAddress = formatFullAddress(details);
      // Current field becomes the street line — unless the originator tagged
      // it 'full', in which case the whole address lands here on one line and
      // there are no street / city / province / postal siblings to speak of,
      // or 'aptStreet', which folds the unit onto the street line while the
      // city / province / postal siblings still fill normally.
      // A plain 'street' field in a block with no apartment slot keeps the
      // unit too — otherwise it would be dropped outright.
      const apartmentField = findFieldByRole('apt');
      onChange(
        field.addressRole === 'full'
          ? fullAddress
          : field.addressRole === 'aptStreet' || !apartmentField
            ? formatStreetLineWithoutApartment(details)
            : details.line1 || '',
      );
      // Sibling population — by role tag, scoped to this field's section.
      // Two Address compounds on the same step are two sections, so each
      // gets its own scoped finder; renaming a label never breaks the
      // mapping. A new selection is authoritative: write every sibling
      // unconditionally so the advisor isn't left with stale values from
      // a previous (possibly wrong) selection.
      const cityField = findFieldByRole('city');
      if (cityField) setValueById(cityField.id, details.city || '');
      if (apartmentField) {
        // Canada Post returns the unit number in the structured
        // `subBuilding` field (e.g. "232" for "232-46 Glamis Green SW"
        // — the BE has already stripped the matching "{subBuilding}-"
        // prefix from line1, so we just write subBuilding here). Fall
        // back to line2 for rural / military / commercial addresses
        // where Canada Post uses Line2 instead of SubBuilding.
        setValueById(apartmentField.id, details.subBuilding || details.line2 || '');
      }
      const postalField = findFieldByRole('postal');
      if (postalField) setValueById(postalField.id, details.postalCode || '');
      const provinceField = findFieldByRole('province');
      if (provinceField) {
        // Province field is typically a SingleSelect whose values are the
        // 2-letter codes (ON, QC, ...). Canada Post returns the code on
        // `provinceCode` directly, so it's a 1:1 set.
        setValueById(provinceField.id, details.provinceCode || '');
      }
      // A separate one-line field in the same block also gets the whole
      // address, so a form can show both the compound AND a flattened copy
      // (some PDFs map a single "Address" AcroForm field). Skipped when the
      // trigger field IS the 'full' field — onChange above already wrote it.
      const fullField = findFieldByRole('full');
      if (fullField && fullField.id !== field.id) setValueById(fullField.id, fullAddress);
      // Same idea one notch narrower: a sibling tagged 'aptStreet' takes the
      // unit + street on one line while city / province / postal keep their
      // own slots. Lets a block carry both a broken-out 'apt' and a combined
      // address line (different AcroForm targets off one retrieve). Skipped
      // when the trigger field IS that field — onChange already wrote it.
      const aptStreetField = findFieldByRole('aptStreet');
      if (aptStreetField && aptStreetField.id !== field.id) {
        setValueById(aptStreetField.id, formatStreetLineWithoutApartment(details));
      }
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AutoComplete
      {...ariaProps}
      value={value}
      style={{ width: '100%' }}
      disabled={disabled}
      // Controlled dropdown so a drill select doesn't strand the user
      // with a hidden list of leaf addresses (AntD closes the dropdown
      // on select, then our follow-up runFind returns 250ms later).
      // The reopen-through-drill trick: when AntD tries to close the
      // dropdown but a drill is in flight, IGNORE the close. That way
      // the dropdown stays open across the drill → leaves transition
      // and AntD never resets its internal state on a hidden dropdown.
      // Non-drill closes (Escape, outside-click, plain leaf select)
      // pass through unchanged.
      open={open}
      onDropdownVisibleChange={(next) => {
        if (!next && drillingRef.current) return;
        setOpen(next);
      }}
      options={options.map((o) => ({
        // `value` is what AntD writes back to the input on selection — keep
        // it the display text so the field never stores Canada Post's
        // opaque id. Repeated-text rows are disambiguated by the React
        // key (array index) plus their description in the label.
        value: o.description ? `${o.text} ${o.description}` : o.text,
        label: o.description ? `${o.text} — ${o.description}` : o.text,
        id: o.id,
        next: o.next,
      }))}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onChange={(v) => handleValueChange(v ?? '')}
      onBlur={handleBlur}
      placeholder={field.placeholder ?? undefined}
      notFoundContent={loading ? '…' : null}
      status={inputStatus}
      // AntD AutoComplete defaults to "filter the options I gave you" —
      // we want it to keep whatever the BE returned, no client-side
      // filtering on top.
      filterOption={false}
    />
  );
}
